import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type SVGProps } from 'react';
import { api, session } from '../../shell/bridge';
/* Card SApyMHlbeyhg — the note's author is a memberId on the row; the readable
   name lives in the cached members list (same source Chores uses), so a byline
   can say WHO saved it without threading a members prop through App. */
import { loadMembers, type Member } from '../../lib/api';
import { Pressable } from '../../ui/Pressable';
/* The routing table itself, not a copy of it (card 4CXp7HfSIAGd): the link card
   names the home its kind routes to, and a second hand-kept map here would drift
   from shared/types.ts the first time a kind moved. */
import { NOTEBOOK_LINK_KINDS, NOTEBOOK_LINK_SUBTYPES, NOTEBOOK_THEME_LABELS, notebookRouteFor } from '../../../../shared/types';
import { SubTabs, SubPanel } from '../../components/SubTabs';
import { Composer } from '../../components/Composer';
import { TabHint } from '../../components/TabHint';
import { markHintSeen } from '../../lib/hints';
import { titleCase } from '../../lib/titleCase';
import { SkeletonBlock } from '../../ui/Skeleton';
import { Tooltip } from '../../ui/Tooltip';
/* Card 9wlZ8NbjACIY — the capture's EXPANDED state is a sheet, so it borrows
   the app's one overlay primitive (and the More sheet's backdrop/panel
   vocabulary) instead of writing its own dialog. */
import { Overlay } from '../../ui/Overlay';
import { GiftingAgent } from './GiftingAgent';
import { detectCaptureType, urlInCapture } from './capture-type';
import { shareCapture, type PearShare } from '../../lib/shareTarget';
import { KudosToast } from '../Requests';
import './notebook.css';

/** 'trailer' is not a category (card 3ThoVHSXNpjo): a trailer is filed under
 *  the thing it trails — TV or a movie — so it has no chip of its own. */
type Category = 'movie' | 'tv' | 'music' | 'podcast' | 'video' | 'article' | 'other';
type Intent = 'play' | 'stream' | 'read' | 'open';
/** Notebook v2 typed collections (design v1.1 §5.3). */
type EntryType = 'link' | 'gift' | 'memory' | 'date' | 'note' | 'idea';

/** Card 2qBAZOM2LFWa — one candidate from the card-detail place search. Names
 *  and addresses only: the worker strips the coordinates before the response
 *  leaves it, so nothing here can carry geo to the browser. */
interface PlacePick { id: string; name: string; address: string | null; types: string[] }

/** Consumed on mount: the compose menu deep-links here to open the right
 *  composer (design v1.1 §5.2 — the + menu is feature discovery).
 *  Card RvESgw7lVASX widened this from note|idea: Memory and Date idea are
 *  quick-add composers of their own, and Link lands on the paste form. */
export interface NotebookComposeIntent {
  kind: 'note' | 'idea' | 'memory' | 'date' | 'link';
}

/** Mood / theme / time tags — the scan proposes them, you apply them. */
type TagKind = 'mood' | 'theme' | 'time';
const TAG_KIND_LABEL: Record<TagKind, string> = { mood: 'Mood', theme: 'Theme', time: 'Time' };

/** Editorial wording for the theme keys (card CTVSTgXykQ0Y) — one vocabulary
 *  for the whole app now (card BS0NIMpbbuC5): the digest's source list reads
 *  the same map, so the notebook card and the digest can never disagree about
 *  what a theme is called. The stored tag, the filter key, the API payload and
 *  every data-* attribute keep the RAW key (shared/types.ts
 *  NOTEBOOK_THEME_VOCAB); keys missing from the map fall back to the raw key —
 *  never blank. */
const THEME_LABEL: Record<string, string> = NOTEBOOK_THEME_LABELS;

/** Display-only render of a tag: themes get the editorial line above, mood
 *  and time tags (free text, never taxonomy) print as typed. */
function tagText(kind: TagKind, tag: string): string {
  return kind === 'theme' ? THEME_LABEL[tag] ?? tag : tag;
}

interface NotebookTag {
  id: string;
  itemId: string;
  kind: TagKind;
  tag: string;
  source: 'human' | 'agent';
  addedBy: string | null;
  createdAt: number;
}

interface Suggestion {
  id: string;
  itemId: string;
  kind: TagKind;
  tag: string;
  rationale: string;
  /** Kept only when it matches a partner's interests profile (card 0OvyJbpnACMX). */
  subject: string | null;
  status: 'pending';
  scanAt: number;
}

interface NotebookItem {
  itemId: string;
  url: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  durationSeconds: number | null;
  provider: string;
  category: Category;
  categoryReason: string;
  categorizedBy: string | null;
  intent: Intent;
  shared: boolean;
  doneAt: number | null;
  addedBy: string;
  createdAt: number;
  updatedAt: number;
  entryType: EntryType;
  body: string | null;
  /** Gift enrichment state (card OviOTaNsNivQ): '' = not a gift, 'pending'
   *  = the search is running, 'done' = detail landed, 'off' = concierge off,
   *  'none' = the search came back empty. */
  enrichStatus: '' | 'pending' | 'done' | 'off' | 'none';
  enrichment: GiftDetail | null;
  /** Links v1 (card 4CXp7HfSIAGd): the kind the card filed under, so the row can
   *  name its home ("Gift ideas") instead of leaving the filing invisible. */
  kind: string;
  subtype: string | null;
  /** Cascade band + ranked candidates (sol review 2026-10-19, card
   *  xNGOA5rZz9LV): 'ask' is an UNRESOLVED card — the deterministic stages
   *  declined to file it, so the card carries the chooser instead of a
   *  filed-looking verdict. Cleared the moment a person answers. */
  kindTier: 'auto' | 'check' | 'ask' | null;
  kindOptions: { kind: string; subtype: string | null; confidence: number | null }[] | null;
  scope: 'shared' | 'just_me';
  /** Lifecycle facet (PRD §6.6) — the Saved / Planned / Done control reads it. */
  status: 'saved' | 'planned' | 'done' | 'passed';
  /** City or "near us" (PRD §5 layer 3); search covers it. */
  place: string | null;
  /** Card 2qBAZOM2LFWa — the day the memory happened, epoch ms. Sent by the
   *  detail form, rendered on the face as one line with the place. */
  eventDate: number | null;
}

/* ── PRD §6.5 E3/E4 — clusters ──────────────────────────────────────────────
   Two cards about the same place, or carrying the same theme, stop reading as
   two unrelated lines: they group themselves into ONE stacked card carrying a
   count ("Brighton Beach, VIC · 4 cards"), which expands in place and offers
   the pair's own actions — Plan this, Pick dates, View on map, Dissolve.

   The grouping is DERIVED on every render over `visible`, the list the server
   already scoped to this viewer (`listNotebook(viewerId)`). A partner's just-me
   card is not in that list, so it cannot enter a cluster this person sees; and
   because nothing is written back, one person's dissolve cannot hide a group
   from the other.

   What IS stored is each person's own opinion OF a group — dissolve / remove
   / add — in their localStorage under their member id, the same place the
   List/Grid pick keeps its own (card F5). A group needs >= 2 cards after those
   edits, so an emptied group simply stops rendering and "Show hidden groups"
   puts everything back. */

interface ClusterPrefs {
  /** Cluster keys this person asked not to see. */
  dissolved: string[];
  /** `key::itemId` — a card pulled out of ONE group (a card stays a card). */
  excluded: string[];
  /** Cluster key -> card ids this person pulled into that group. */
  added: Record<string, string[]>;
}

const EMPTY_CLUSTER_PREFS: ClusterPrefs = { dissolved: [], excluded: [], added: {} };

const clusterPrefKey = (memberId: string | null) => `pear.nb.clusters.${memberId ?? 'anon'}`;
const clusterExclKey = (clusterKey: string, itemId: string) => `${clusterKey}::${itemId}`;

function readClusterPrefs(memberId: string | null): ClusterPrefs {
  if (typeof window === 'undefined') return EMPTY_CLUSTER_PREFS;
  try {
    const raw = window.localStorage.getItem(clusterPrefKey(memberId));
    if (!raw) return EMPTY_CLUSTER_PREFS;
    const p = JSON.parse(raw) as Partial<ClusterPrefs>;
    return {
      dissolved: Array.isArray(p.dissolved) ? p.dissolved.filter(x => typeof x === 'string') : [],
      excluded: Array.isArray(p.excluded) ? p.excluded.filter(x => typeof x === 'string') : [],
      added: p.added && typeof p.added === 'object' && !Array.isArray(p.added)
        ? Object.fromEntries(Object.entries(p.added as Record<string, unknown>)
            .filter(([, v]) => Array.isArray(v))
            .map(([k, v]) => [k, (v as unknown[]).filter((x): x is string => typeof x === 'string')]))
        : {},
    };
  } catch { return EMPTY_CLUSTER_PREFS; }
}

/** Group keys are case- and space-blind ("Brighton Beach, VIC" and
 *  "brighton  beach, vic" are one group); the LABEL keeps the first member's
 *  own casing, so the cluster names the place the way it was typed. */
function clusterNorm(s: string): string {
  return s.trim().replace(/\s+/g, ' ').toLowerCase();
}

interface FeedCluster {
  key: string;
  kind: 'place' | 'theme';
  label: string;
  members: NotebookItem[];
  /** Distinct places among the members — the "· 4 places" of PRD E3. */
  places: number;
}

function clusterTitleOf(item: NotebookItem): string {
  return item.entryType === 'link'
    ? (item.title?.trim() || hostOf(item.url))
    : (item.body?.trim() || ENTRY_LABEL[item.entryType]);
}

function deriveClusters(
  items: NotebookItem[],
  tags: NotebookTag[],
  prefs: ClusterPrefs,
): FeedCluster[] {
  const groups = new Map<string, { kind: FeedCluster['kind']; label: string; members: NotebookItem[] }>();
  const byId = new Map(items.map(i => [i.itemId, i]));

  const join = (key: string, kind: FeedCluster['kind'], label: string, item: NotebookItem) => {
    const g = groups.get(key) ?? { kind, label, members: [] };
    if (!g.members.some(m => m.itemId === item.itemId)) g.members.push(item);
    groups.set(key, g);
  };

  for (const item of items) {
    const place = (item.place ?? '').trim();
    if (place) join(`place:${clusterNorm(place)}`, 'place', place, item);
  }
  for (const t of tags) {
    if (t.kind !== 'theme') continue;
    const item = byId.get(t.itemId);
    if (item) join(`theme:${clusterNorm(t.tag)}`, 'theme', tagText('theme', t.tag), item);
  }
  /* E4 "add members" — a card pulled into a group it does not mechanically
     belong to. Only ever edits this person's own view of the feed. */
  for (const [key, ids] of Object.entries(prefs.added)) {
    const g = groups.get(key);
    if (!g) continue;
    for (const id of ids) {
      const item = byId.get(id);
      if (item && !g.members.some(m => m.itemId === id)) g.members.push(item);
    }
  }

  const dissolved = new Set(prefs.dissolved);
  const out: FeedCluster[] = [];
  for (const [key, g] of groups) {
    if (dissolved.has(key)) continue;
    const members = g.members.filter(m => !prefs.excluded.includes(clusterExclKey(key, m.itemId)));
    if (members.length < 2) continue;
    const places = new Set(members.map(m => (m.place ?? '').trim()).filter(Boolean));
    out.push({ key, kind: g.kind, label: g.label, members, places: places.size });
  }
  /* Concrete first — a resolved place outranks a theme — then the bigger
     groups, so a live list keeps a stable, most-useful-first order. */
  return out.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'place' ? -1 : 1;
    return b.members.length - a.members.length;
  });
}

/** Mirrors GIFT_ACK in shared/gift-enrich.ts — the web bundle types its own
 *  surface rather than importing shared (same reason as INVITE_ALPHABET in
 *  Pair.tsx). scripts/probe-gift-enrich.mjs asserts the two stay identical,
 *  so a rewrite of the line in one place fails the probe instead of shipping
 *  a card that says something the server never sent. */
export const GIFT_ACK = "I've noted your gift idea. Please allow me some time to develop an initial list of suggestions. I'll be back to you shortly.";

/** Mirrors GiftEnrichment in shared/types.ts. */
interface GiftDetail {
  source: 'serpapi' | 'apiserpent';
  query: string;
  priceBand: string;
  why: string;
  picks: { title: string; price: string | null; url: string; rating: string | null }[];
  count: number;
  at: number;
}

/** The Vault half of the Date Planner (card ZdYBVP203b8a) — the fields the
 *  pool list under the Date tile reads. Mirrors DateView in shared/types.ts;
 *  the web bundle types its own surface rather than importing shared. */
interface DateIdeaRow {
  ideaId: string;
  title: string;
  note: string | null;
  source: string;
  mode: string;
  costBand: string;
  energy: string;
  provenance: string | null;
  state: string;
}

const DATE_MODE_LABEL: Record<string, string> = { stay_in: 'In', go_out: 'Out', virtual: 'Virtual' };

function dateMeta(d: DateIdeaRow): string {
  const bits: string[] = [];
  if (d.costBand) bits.push(d.costBand === 'free' ? 'Free' : d.costBand);
  if (d.mode) bits.push(DATE_MODE_LABEL[d.mode] ?? d.mode);
  return bits.join(' · ');
}

/** Display only (card CTVSTgXykQ0Y) — the chip, the retag prompt and the
 *  select all read these; the posted `category` stays the raw key above. */
const CATEGORY_LABEL: Record<Category, string> = {
  movie: 'Movie', tv: 'TV', music: 'Music',
  podcast: 'Podcast', video: 'Video', article: 'Article', other: 'Something else',
};

const INTENT_VERB: Record<Intent, string> = {
  play: 'Play', stream: 'Stream', read: 'Read', open: 'Open',
};

/** Founder email 2026-10-19: the done verb is context aware — a link you
 *  Watched, Listened to or Read says so; anything else is honestly "Done".
 *  Read straight off the card's own kind, so it can never disagree with the
 *  filing. */
const KIND_DONE_VERB: Record<string, string> = {
  watch: 'Watched', listen: 'Listened', read: 'Read', play: 'Played',
  learn: 'Learned', make: 'Made',
};
const doneVerb = (kind: string | null): string => (kind ? KIND_DONE_VERB[kind] ?? 'Done' : 'Done');

/** Sol review 2026-10-19: the correction control read the legacy taxonomy
 *  while the card headline read the kind — two vocabularies on one card.
 *  Each legacy option now names the filing it lands (linkFacets mapping). */
const CATEGORY_TO_KIND: Record<Category, string> = {
  movie: 'watch', tv: 'watch', video: 'watch',
  music: 'listen', podcast: 'listen', article: 'read', other: 'other',
};

function durationLabel(s: number | null): string | null {
  if (!s) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
}

function hostOf(u: string): string {
  try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return u; }
}

/** PRD E1/E2 — one place string from a search candidate. Google's formatted
 *  address usually already leads with the place name, so prefixing it again
 *  put "Brighton Beach, Brighton Beach, VIC …" on the card; the name only
 *  leads when the address does not carry it. Both editors (the card's detail
 *  form and the panel's correction) build the string through here. */
function placeFromPick(p: PlacePick): string {
  if (!p.address) return p.name;
  return p.address.toLowerCase().startsWith(p.name.toLowerCase()) ? p.address : `${p.name}, ${p.address}`;
}

/** Card FsDDh1AF6Ege — date-added on every notebook card, straight from the
 *  item's own record. Day + month + year, no clock time: "when we saved it"
 *  is a day-level fact. Same shape the chores timeline prints. */
function addedLabel(at: number): string {
  const d = new Date(at);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Card SApyMHlbeyhg — the note's byline: WHO saved it, and the moment they
 *  did, to the minute ("By Partner A, 16 October 2026 12:06 PM"). Unlike the
 *  day-level Added line above, attribution is a precise instant — the same
 *  note can be saved by either partner within one evening, and the byline is
 *  what tells them apart. */
function bylineLabel(at: number): string {
  const d = new Date(at);
  const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} ${time}`;
}

/** Resolve a note's author memberId to a readable name. "You" for the viewer's
 *  own row, the partner's display name otherwise, and a neutral fallback for a
 *  row whose author is not in the cached members list yet. */
function bylineAuthor(addedBy: string | null, members: Member[], meId: string | null): string {
  if (!addedBy) return 'Someone';
  if (meId && addedBy === meId) return 'You';
  const m = members.find(x => x.memberId === addedBy);
  return m?.displayName?.trim() || 'Your partner';
}

/** The partner's display name for the visibility pill ("Shared with <name>").
 *  The other member of the couple — fallback "your partner" when the cache has
 *  only this member (fresh pair, before the members list fills). */
function partnerNameOf(members: Member[], meId: string | null): string {
  const other = members.find(x => x.memberId !== meId);
  return other?.displayName?.trim() || 'your partner';
}

/**
 * Visibility pill (card quWhYyrNiIbt, PRD K3) — one control on the card face
 * that reads the card's audience ("Shared with <partner>" / "Just you") and,
 * on click, opens a small menu to change it. It REPLACES the old
 * "Make just me" / "Share with partner" action pair: the pill states the
 * current truth instead of offering whichever single flip happened to be
 * missing. Owner-only (same `canRemove` gate the pair used) and the flip still
 * routes through the existing confirm + owner-only PUT, so nothing about the
 * server contract changes — only the affordance.
 */
function VisibilityPill({ shared, partnerName, busy, onSet }: {
  shared: boolean;
  partnerName: string;
  busy: boolean;
  onSet: (next: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Close on outside press / Escape so the menu never traps the card.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  const label = shared ? `Shared with ${partnerName}` : 'Just you';
  return (
    <div className="nb-vis-pill" ref={ref}>
      <button
        type="button"
        className={`nb-vis-pill-btn${shared ? ' is-shared' : ' is-private'}`}
        data-test="nb-visibility"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Who can see this — ${label}. Change it.`}
        disabled={busy}
        onClick={() => setOpen(o => !o)}
      >
        <span className="nb-vis-dot" aria-hidden="true" />
        {label}
      </button>
      {open && (
        <div className="nb-vis-menu" role="menu" aria-label="Who can see this">
          <button
            type="button"
            role="menuitemradio"
            aria-checked={shared}
            className={`nb-vis-opt${shared ? ' is-on' : ''}`}
            data-test="nb-vis-shared"
            onClick={() => { setOpen(false); if (!shared) onSet(true); }}
          >
            Shared with {partnerName}
          </button>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={!shared}
            className={`nb-vis-opt${!shared ? ' is-on' : ''}`}
            data-test="nb-vis-private"
            onClick={() => { setOpen(false); if (shared) onSet(false); }}
          >
            Just you
          </button>
        </div>
      )}
    </div>
  );
}

/** Notebook v2 collections (design v1.1 §5.3/§7) — quick-add types shown as
 *  quick tiles plus the link paste. 'all' shows everything.
 *
 *  The Date tile is the Vault half of the Date Planner (card ZdYBVP203b8a,
 *  PRD §3): Vault captures, Us → Dates plans, and both are the SAME
 *  DateIdea pipeline. So the tile is named for what it makes — a "Date
 *  idea" — and saving one posts to /api/dates, not /api/notebook. */
const ENTRY_TILES: Array<{ key: EntryType | 'all'; label: string }> = [
  { key: 'all', label: 'Everything' },
  { key: 'link', label: 'Link' },
  { key: 'gift', label: 'Gift' },
  { key: 'memory', label: 'Memory' },
  { key: 'date', label: 'Date idea' },
  { key: 'note', label: 'Note' },
  { key: 'idea', label: 'Idea' },
];

/** Vector icon for a typed card. The emoji set (🔗🎁🫶📅📝💡) rendered
 *  differently per platform and duplicated what the pill row already says —
 *  card tESKXV2eqHdV asked for vectors. */
const ICON_ATTRS: SVGProps<SVGSVGElement> = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true as const,
  focusable: 'false' as const,
};

const ENTRY_ICON: Record<EntryType, ReactNode> = {
  link: (
    <svg {...ICON_ATTRS}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  gift: (
    <svg {...ICON_ATTRS}>
      <polyline points="20 12 20 22 4 22 4 12" />
      <rect x="2" y="7" width="20" height="5" />
      <line x1="12" y1="22" x2="12" y2="7" />
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
  ),
  memory: (
    <svg {...ICON_ATTRS}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  date: (
    <svg {...ICON_ATTRS}>
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  note: (
    <svg {...ICON_ATTRS}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  idea: (
    <svg {...ICON_ATTRS}>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M12 2a6 6 0 0 0-3.4 10.9c.8.6 1.4 1.6 1.4 2.6h4c0-1 .6-2 1.4-2.6A6 6 0 0 0 12 2z" />
    </svg>
  ),
};

/**
 * Per-tab id override for the collection row (UX item 6): the Note / Idea
 * tiles keep the ids the quick-add composer and the walks already target, so
 * the panel's `aria-labelledby` resolves to exactly the string the tab
 * renders. ONE function, handed to both SubTabs and SubPanel — two spellings
 * of the same pattern are two chances to drift apart.
 */
const nbSubTabId = (key: string): string | undefined =>
  key === 'note' ? 'nb-tile-note' : key === 'idea' ? 'nb-tile-idea' : undefined;

/** PRD E1/K1 — the pin that leads a place entity chip. One glyph, drawn on
 *  the chip's tint (D2 keeps chips out of the accent roles), so a card that
 *  resolved a place reads as a place without spending a word of text. */
const PLACE_PIN: ReactNode = (
  <svg {...ICON_ATTRS} width={12} height={12}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ENTRY_LABEL: Record<EntryType, string> = {
  link: 'Link', gift: 'Gift', memory: 'Memory', date: 'Date', note: 'Note', idea: 'Idea',
};

/** The types the capture bar's chip offers (PRD §6.2 C2). Link is missing on
 *  purpose: it is the one type the TEXT decides (a URL cannot be saved as
 *  words), so a URL capture shows "Link" and the menu offers everything it
 *  could be filed as instead. */
const CAPTURE_TYPES: EntryType[] = ['gift', 'memory', 'date', 'note', 'idea'];

/** A transcription card (a0ABwZvTf9ct) keeps `entry_type: note` — a new
 *  entry_type would need a CHECK-constraint migration — and is recognised by
 *  `provider: 'meeting'`, which only the server-side /api/transcribe route
 *  writes. So the meeting gets its own label and icon outside the Record. */
const MEETING_ICON: ReactNode = (
  <svg {...ICON_ATTRS}>
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <line x1="12" y1="18" x2="12" y2="22" />
  </svg>
);
const MEETING_LABEL = 'Meeting';
/** Plural forms — `ENTRY_LABEL + 's'` would render "Memorys save to…". */
const ENTRY_PLURAL: Record<EntryType, string> = {
  link: 'Collection', gift: 'Gifts', memory: 'Memories', date: 'Dates', note: 'Notes', idea: 'Ideas',
};

/** Empty-state first-move line per collection (card 7VIbSBeJhvbQ). The old
 *  single line was link copy ("drop a trailer … an album"), so it named a
 *  link on the Gift / Memory / Date / Note / Idea tabs where a link cannot
 *  be added at all. `requestedCollection` is a plain string from the More
 *  menu, so anything unrecognised falls back to the Everything line rather
 *  than rendering `undefined`. */
const EMPTY_HINT: Record<'all' | EntryType, string> = {
  all: 'Drop a trailer you both want to see, or an album you keep coming back to.',
  link: 'Drop a trailer you both want to see, or an album you keep coming back to.',
  gift: 'Add a gift idea — the concierge goes looking for suggestions.',
  memory: 'Add a memory — a moment you both want to keep.',
  date: 'Add a date idea — it waits here until you plan together.',
  note: 'Add a note — anything you both want on the record.',
  idea: 'Add an idea — a trip, a plan, a thing to do one day.',
};

/* ── Context panel (card 2wn9seit8zGi, PRD §6.4) ──────────────────────────
   The third zone of the desktop shell. P1: selecting a card opens it here and
   Esc closes it. P2: who saved it, when, what it is, who can see it, and the
   source. P4: the next steps that actually exist for this row. P5: with
   nothing selected it is NEVER an empty box — "Concierge suggests" (the scan
   results already waiting on the tags endpoint) over "Recently saved".

   It renders only at >=760px (PRD L5) and is a real landmark, not a tooltip:
   the feed stays where it is, the panel scrolls on its own, and one item is
   open at a time by construction — `contextId` is a single string. */
function NotebookContext({ item, suggestions, recent, members, meId, busy, onClose, onOpen, onDone, onRemove, onEdit, onPlaceSave }: {
  item: NotebookItem | null;
  suggestions: Suggestion[];
  recent: NotebookItem[];
  members: Member[];
  meId: string | null;
  busy: boolean;
  onClose: () => void;
  onOpen: (itemId: string) => void;
  onDone: (item: NotebookItem) => void;
  onRemove: (item: NotebookItem) => void;
  onEdit: (item: NotebookItem) => void;
  /** PRD E2 — the correction half of the panel's entity chip. The parent owns
   *  the reload, so the panel never mutates the feed's own copy. */
  onPlaceSave: (item: NotebookItem, place: string) => Promise<void>;
}) {
  /* PRD P2 asks for an editable title and notes. The panel is the read face
     and the editor is the card's own inline form (one editor, one source of
     truth) — "Edit" hands back to the feed rather than growing a second copy
     of the same PUT. */
  const isOwner = item !== null && meId !== null && item.addedBy === meId;
  const title = item
    ? (item.entryType === 'link' ? (item.title?.trim() || hostOf(item.url)) : (item.body?.trim() || ENTRY_LABEL[item.entryType]))
    : '';

  /* PRD E1/E2 — the entity chip on the panel, and the correction behind it.
     The resolution itself runs on the card's own places route (the dev mock
     answers here, so no spend), and the save goes through the parent so the
     feed re-reads its own rows instead of this panel editing a copy. */
  const [correcting, setCorrecting] = useState(false);
  const [correctText, setCorrectText] = useState('');
  const [correctSearchBusy, setCorrectSearchBusy] = useState(false);
  const [correctBusy, setCorrectBusy] = useState(false);
  const [correctNote, setCorrectNote] = useState<string | null>(null);
  const [correctResults, setCorrectResults] = useState<PlacePick[]>([]);
  const [correctError, setCorrectError] = useState<string | null>(null);

  const openCorrect = () => {
    if (!item) return;
    setCorrectText(item.place ?? '');
    setCorrectResults([]);
    setCorrectNote(null);
    setCorrectError(null);
    setCorrecting(true);
  };

  const closeCorrect = () => {
    setCorrecting(false);
    setCorrectResults([]);
    setCorrectNote(null);
    setCorrectError(null);
  };

  /** One search, same route and same plain-words fallbacks the card's own
   *  detail form uses — two spellings of that message would drift. */
  const searchCorrect = async () => {
    const text = correctText.trim();
    if (!text || correctSearchBusy) return;
    setCorrectSearchBusy(true);
    setCorrectNote(null);
    try {
      const res = await api<{ places: PlacePick[]; capped: boolean; reason: string | null }>(
        '/api/notebook/places',
        { method: 'POST', body: { text } },
      );
      setCorrectResults(res.places ?? []);
      if (res.capped) {
        setCorrectNote("That's today's location search limit — the plain text box still works.");
      } else if ((res.places ?? []).length === 0) {
        setCorrectNote("Nothing came back for that — type the place in yourself and it'll save fine.");
      }
    } catch (err) {
      console.error('[Pear] panel place search failed', err);
      setCorrectNote("Couldn't search just now — type the place in yourself.");
      setCorrectResults([]);
    } finally {
      setCorrectSearchBusy(false);
    }
  };

  const saveCorrect = async (target: NotebookItem) => {
    if (correctBusy) return;
    setCorrectBusy(true);
    setCorrectError(null);
    try {
      await onPlaceSave(target, correctText.trim());
      closeCorrect();
    } catch (err) {
      console.error('[Pear] panel place save failed', err);
      setCorrectError("That correction didn't save — what's on the card is unchanged. Try again.");
    } finally {
      setCorrectBusy(false);
    }
  };

  return (
    <aside className="nb-context" id="nb-context" aria-label="Card details" data-testid="nb-context">
      {item === null ? (
        /* P5 — the never-empty default. */
        <div className="nb-context-idle">
          <section className="nb-context-mod" aria-labelledby="nb-ctx-suggest-head">
            <h3 className="nb-context-head" id="nb-ctx-suggest-head">Concierge suggests</h3>
            {suggestions.length === 0 ? (
              <p className="nb-context-empty">
                Nothing to suggest yet — save a link and I'll read it while you work.
              </p>
            ) : (
              <ul className="nb-context-list">
                {suggestions.slice(0, 3).map(s => (
                  <li key={s.id}>
                    <button type="button" className="nb-context-row" onClick={() => onOpen(s.itemId)}>
                      <span className="nb-context-row-title">{s.subject || tagText(s.kind, s.tag)}</span>
                      <span className="nb-context-row-why">{s.rationale}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="nb-context-mod" aria-labelledby="nb-ctx-recent-head">
            <h3 className="nb-context-head" id="nb-ctx-recent-head">Recently saved</h3>
            {recent.length === 0 ? (
              <p className="nb-context-empty">Your first card will show up here.</p>
            ) : (
              <ul className="nb-context-list">
                {recent.slice(0, 4).map(r => (
                  <li key={r.itemId}>
                    <button type="button" className="nb-context-row" onClick={() => onOpen(r.itemId)}>
                      <span className="nb-context-row-title">
                        {r.entryType === 'link' ? (r.title?.trim() || hostOf(r.url)) : (r.body?.trim() || ENTRY_LABEL[r.entryType])}
                      </span>
                      <span className="nb-context-row-why">{bylineLabel(r.createdAt)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      ) : (
        <div className="nb-context-item" data-testid="nb-context-item">
          <div className="nb-context-top">
            <span className={`nb-cat nb-cat-${item.entryType}`}>
              <span className="nb-icon" aria-hidden="true">{ENTRY_ICON[item.entryType]}</span>
              {ENTRY_LABEL[item.entryType]}
            </span>
            <button type="button" className="nb-context-close" data-test="nb-context-close" onClick={onClose}>
              Close
            </button>
          </div>

          <h3 className="nb-context-title">{title}</h3>

          <p className="nb-context-meta">
            By {bylineAuthor(item.addedBy, members, meId)}, {bylineLabel(item.createdAt)}
            {' · '}
            <span className={item.shared ? 'nb-context-vis-shared' : 'nb-context-vis-private'} data-testid="nb-context-visibility">
              {item.shared ? 'Shared' : 'Just you'}
            </span>
          </p>

          {/* P2's source line — a link card keeps the door it came through. */}
          {item.entryType === 'link' && (
            <a className="nb-context-source" href={item.url} target="_blank" rel="noreferrer noopener" data-test="nb-context-source">
              {hostOf(item.url)} ↗
            </a>
          )}

          {item.description && <p className="nb-context-note">{item.description}</p>}

          {/* PRD E1/E2 — the resolved place rides beside the words it came
              from, as a chip the eye can pick out of the panel. A card with
              no place renders no chip: unresolved text shows exactly as it
              was typed, and the correction door is owner-only (same gate as
              Edit, because it writes). */}
          {(item.place || isOwner) && (
            <div className="nb-ctx-entities" data-test="nb-ctx-entity-chips">
              {item.place && (
                <span className="nb-entity-chip" data-entity="place" data-testid="nb-context-place">
                  {PLACE_PIN}
                  {item.place}
                </span>
              )}
              {isOwner && !correcting && (
                <button type="button" className="nb-entity-fix" data-test="nb-correct" onClick={openCorrect}>
                  {item.place ? 'Correct' : 'Add a place'}
                </button>
              )}
            </div>
          )}

          {/* The correction itself — search against the card's own places
              route (the dev mock answers here) or type the name yourself;
              either way the save is one PUT on the card's edit verb. */}
          {correcting && item && (
            <div className="nb-correct-form" data-test="nb-correct-form">
              <label className="nb-label" htmlFor="nb-correct-place">Where?</label>
              <div className="nb-place-row">
                <input
                  id="nb-correct-place"
                  className="pear-chat-input nb-input"
                  data-test="nb-correct-place"
                  placeholder="Search for a place, or type it in"
                  value={correctText}
                  onChange={e => setCorrectText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void searchCorrect();
                    }
                  }}
                />
                <Pressable
                  className="pear-btn nb-place-search"
                  data-test="nb-correct-search"
                  disabled={correctSearchBusy || !correctText.trim()}
                  onClick={() => void searchCorrect()}
                >
                  {correctSearchBusy ? 'Searching…' : 'Search'}
                </Pressable>
              </div>
              {correctNote && <p className="nb-hint" data-test="nb-correct-note">{correctNote}</p>}
              {correctResults.length > 0 && (
                <ul className="nb-place-results" data-test="nb-correct-results">
                  {correctResults.map(p => (
                    <li key={p.id}>
                      <button
                        type="button"
                        className="nb-place-result"
                        data-test="nb-correct-result"
                        onClick={() => {
                          setCorrectText(placeFromPick(p));
                          setCorrectResults([]);
                          setCorrectNote(null);
                        }}
                      >
                        <span className="nb-place-name">{p.name}</span>
                        {p.address && <span className="nb-place-addr">{p.address}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {correctError && (
                <p className="nb-save-error" data-test="nb-correct-error" role="alert">{correctError}</p>
              )}
              <div className="nb-composer-actions">
                <Pressable className="pear-btn" data-test="nb-correct-cancel" onClick={closeCorrect}>
                  Cancel
                </Pressable>
                <Pressable
                  className="pear-btn pear-btn-mustard"
                  data-test="nb-correct-save"
                  disabled={correctBusy}
                  onClick={() => void saveCorrect(item)}
                >
                  {correctBusy ? 'Saving…' : 'Save'}
                </Pressable>
              </div>
            </div>
          )}

          {/* P4 — next steps that actually do something. No speculative
              concierge verbs: every control here hits an endpoint the card
              already owns. */}
          <div className="nb-context-steps" aria-label="Next steps">
            <h4 className="nb-context-steps-head">Next steps</h4>
            {item.entryType === 'link' && (
              <a className="nb-context-step" href={item.url} target="_blank" rel="noreferrer noopener">
                {INTENT_VERB[item.intent] === 'Open' ? 'Open' : `Open ${INTENT_VERB[item.intent]}`} ↗
              </a>
            )}
            <button
              type="button"
              className="nb-context-step"
              data-test="nb-context-done"
              disabled={busy}
              onClick={() => onDone(item)}
            >
              {item.status === 'done' ? 'Mark not done' : `Mark ${doneVerb(item.kind)}`}
            </button>
            {/* Audience is stated ONCE, in the meta line above (founder email
                2026-10-23: "just me is listed twice — doesn't need to be a
                pill"). The card face keeps the VisibilityPill as the control;
                the panel is the read face (PRD §6.4). */}
            {isOwner && (
              <button type="button" className="nb-context-step" data-test="nb-context-edit" disabled={busy} onClick={() => onEdit(item)}>
                Edit
              </button>
            )}
            {isOwner && (
              <button type="button" className="nb-context-step nb-context-step-quiet" data-test="nb-context-remove" disabled={busy} onClick={() => onRemove(item)}>
                Remove
              </button>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

/**
 * Shared Notebook — paste a link, it becomes a card for both partners
 * (epic szvNTWusHt3f), OR quick-add a typed plain-text entry (gift / memory /
 * date / note / idea — design v1.1 §5.3). The compose menu's Note / Idea tiles
 * deep-link here via `intent` and open the right quick-add composer.
 */
export function NotebookPanel({ intent, requestedCollection, pendingShare, onShareConsumed }: {
  intent?: NotebookComposeIntent | null;
  /** Quick link from the More menu (card yOcdjnR6pXBz): land on this
   *  collection. null = no request. Does NOT open a composer — the link is
   *  a filter, not an add. */
  requestedCollection?: string | null;
  /** A share from another app (PWA share_target, card jZn7vpkB7EG1), parked
   *  until this screen could hold it. null = nothing parked. */
  pendingShare?: PearShare | null;
  /** Called once the parked share has been applied — the payload is dropped. */
  onShareConsumed?: () => void;
}) {
  const me = session();
  /* Card SApyMHlbeyhg — the cached members list, so a note's byline can name
     its author. The same localStorage cache Chores reads (no new fetch, no App
     plumbing); recomputed each render is a cheap synchronous read. */
  const members = loadMembers();
  const [items, setItems] = useState<NotebookItem[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  /* Desktop uplift phase 2b (card quWhYyrNiIbt item 1, PRD §6.2) — ONE capture
     bar replaces the two composers (typed quick-add + link paste).

     `quickType` is now the bar's TYPE: null means "let the text decide"
     (detectCaptureType — a URL is a link, a price is a gift, words are a
     note), and any explicit choice — the chip, or picking a collection tab —
     pins it. `draft` is the single text field both old composers shared.
     `justSavedId` drives the insertion highlight (C5), and `captureToast` is
     the saved-with-Undo line that follows a typed save home. */
  const [quickType, setQuickType] = useState<EntryType | null>(null);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [collection, setCollection] = useState<'all' | EntryType>('all');
  const [justSavedId, setJustSavedId] = useState<string | null>(null);
  const [captureToast, setCaptureToast] = useState<{ itemId: string | null; text: string } | null>(null);
  const captureToastTimer = useRef<number | undefined>(undefined);
  // Visibility of the next typed entry (card sYwPmYEIf4VC): 'us' is the
  // shared notebook, 'me' is private to this member.
  const [sharedDraft, setSharedDraft] = useState<'us' | 'me'>('us');
  /* Card ggTQe2KbB0A3 HIGH-3 — a GIFT capture keeps its own visibility, and
     it starts on 'me'. The shared draft is reset to 'us' after every save, so
     a gift typed straight after an ordinary note silently inherited 'us' and
     posted `shared: true` on the wire — exactly the widening the server's
     private default (card aDlJX4X7txMI) exists to prevent. One state per
     family, each reset to its own default: a surprise cannot ride in on the
     tail of an unrelated save. */
  const [giftDraft, setGiftDraft] = useState<'us' | 'me'>('me');
  /* The visibility the NEXT save will actually send — read once at the point
     of use so the radio, the hint and the request can never disagree. */
  const draftSharedFor = (t: EntryType): 'us' | 'me' =>
    t === 'gift' ? giftDraft : t === 'date' ? 'us' : sharedDraft;
  /* Card 67zOnRgx4YTI item 1 — the notebook was create-only, so a typo meant
   * a second card. One editor per card: which card is open, its title and its
   * text (links carry both, typed entries carry the body). */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  /* Founder email 2026-10-23: the edit box grows with what is written — at
     rows=3 it showed a scrollbar at three lines. Same scrollHeight mechanism
     the Composer owns; one edit is open at a time (editingId), so one ref
     serves both the typed-card and link edit forms. Past the 40vh cap the
     native scrollbar takes over (textarea.pear-chat-input's overflow), and
     below it the box simply grows. */
  const editTextRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = editTextRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [editText, editingId]);
  /* Card 2qBAZOM2LFWa — a memory knows the day it happened and where. Both
     ride the card's own detail: a small pill on the face opens one disclosure
     holding a date and a place search, and the saved pair comes back to the
     face as one line. One card at a time, same rule as the text editor — two
     open detail forms on one screen is a form the user did not ask for.
     `placeResults` holds the search's candidates; they are names and
     addresses only, because the route strips the coordinates before they
     leave the worker. */
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailDate, setDetailDate] = useState('');
  const [detailPlace, setDetailPlace] = useState('');
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeBusy, setPlaceBusy] = useState(false);
  const [placeResults, setPlaceResults] = useState<PlacePick[]>([]);
  const [placeNote, setPlaceNote] = useState<string | null>(null);
  /* The receipt for the link just saved (card VPlGeOn7LJPI, PRD §6.1): "Filed to
   * Events › Gigs · Shared. Change." Undo is here, not in a dialog, because the
   * one thing a mis-paste needs is the row gone. The card exists before its
   * verdict does, so the receipt starts as "still reading" and refines to the
   * home the routing table sent it to once the classifier answers. */
  const [receipt, setReceipt] = useState<{
    itemId: string;
    kind: string;
    scope: 'shared' | 'just_me';
    filing: boolean;
    undone: boolean;
    /** A mutation is in flight — both buttons are disabled while it runs. */
    busy: boolean;
    /** The last mutation's failure, said out loud (sol review 2026-10-17):
     *  a console line is not a receipt. */
    error: string | null;
    /** The cascade band the poll saw (card xNGOA5rZz9LV phase 3): an 'ask'
     *  row must say it is unsure, and a 'check' row must not read as settled. */
    tier: 'auto' | 'check' | 'ask' | null;
  } | null>(null);
  // Mood / theme / time tags and whatever the last scan left waiting (card
  // ioKcDXGeHnE7) — same fetch cadence as the cards themselves.
  const [tags, setTags] = useState<NotebookTag[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [busySugg, setBusySugg] = useState<string | null>(null);
  const [overriding, setOverriding] = useState<string | null>(null);
  const [overrideText, setOverrideText] = useState('');
  // The Vault → Date idea pool (card ZdYBVP203b8a, PRD §6.1): ideas captured
  // here live in the SAME pipeline Us → Dates plans from, so the Date tile
  // shows where they went instead of swallowing them.
  const [dateIdeas, setDateIdeas] = useState<DateIdeaRow[] | null>(null);
  // PRD §6.1 promises a toast on save — the confirmation is the whole
  // contract for a <10s capture you may not look at again.
  const [dateSaved, setDateSaved] = useState(false);
  // Card o79UE71eZgBs: a save used to CLOSE the typed composer, so capturing
  // three memories meant re-opening it three times and the quick entry
  // vanished the moment it did its job. The composer now stays open with the
  // field cleared, and this carries the short inline confirmation naming the
  // composer that answered — it is a type, not a boolean, because the line
  // reads "Saved to your memories" / "…your gifts".
  const [quickSaved, setQuickSaved] = useState<EntryType | null>(null);
  const quickSavedTimer = useRef<number | undefined>(undefined);
  // Gift enrichment poll budget (card OviOTaNsNivQ): the search runs AFTER
  // the save has answered, so the card starts 'pending' and only a refetch
  // can see the detail land. Bounded so a stalled engine cannot turn into an
  // infinite loop of GETs.
  const enrichPasses = useRef(0);
  /* Sol review 2026-10-19 (card xNGOA5rZz9LV phase 3, high): add / quickAdd /
   * toggleDone used to report a failure to the console only — the screen looked
   * exactly like a success, the draft was still sitting there, and the natural
   * reaction was to press Save again and duplicate the row. Each surface now
   * owns its own out-loud error. The drafts are ALREADY preserved on failure
   * (they only clear on a confirmed save), so the message is the missing half. */
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [quickError, setQuickError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ itemId: string; message: string } | null>(null);
  const rowErrorTimer = useRef<number | undefined>(undefined);
  /* Card xUZFWf8JtHo2 item 5 — quiet remove. Removing a card no longer opens a
     confirm modal; it acts immediately and leaves a "Removed — Undo" line for a
     few seconds. Removal is a hard DELETE server-side, so Undo re-creates the
     row from the fields kept here (POST /api/notebook) rather than flipping a
     soft-delete flag. Undo is the safety net a modal used to be, without the
     interruption. */
  const [removedItem, setRemovedItem] = useState<{ item: NotebookItem; label: string } | null>(null);
  const undoTimer = useRef<number | undefined>(undefined);
  /* Newest-response guard for reload(): the unfurl polls and a mutation's own
   * refetch all call it, and an older answer landing after a newer one used to
   * resurrect a row that had just been removed. Only the latest call applies. */
  const reloadSeq = useRef(0);
  /** Kept current so a callback defined before `reload` (the capture toast's
   *  Undo) can refetch without depending on the render order. */
  const reloadRef = useRef<(() => Promise<void>) | null>(null);
  /* ── Desktop uplift phase 2 (card 2wn9seit8zGi, PRD §6.1 + §6.4) ──────────
     The context panel is the third zone of the desktop shell. `contextId` is
     the ONE card open in it — the PRD is explicit that only one item is open
     at a time (P1), and that Esc closes it. Below 760px there is no third
     zone at all (PRD L5: the existing mobile layout applies), so the panel
     simply does not render and these state writes cost nothing. */
  const [contextId, setContextId] = useState<string | null>(null);
  /* PRD §8 — which card holds the feed's single tab stop (roving tabindex):
     Tab reaches the feed once, Arrow keys walk it. */
  const [feedRoving, setFeedRoving] = useState<string | null>(null);
  /* PRD L1/L5 hinge on the viewport: >=1100px the panel is a sticky third
     column, 760-1100px it is a right slide-over, <760px it does not exist.
     Read once, then kept current — a resize across the hinge has to move the
     panel, not leave a fixed drawer stranded on a wide canvas. */
  const [viewportW, setViewportW] = useState<number>(() =>
    typeof window === 'undefined' ? 0 : window.innerWidth);
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const contextLive = viewportW >= 760;
  const contextInline = viewportW >= 1100;
  /* PRD F5 — List/Grid, remembered per user. Read in the lazy initializer so
     the first paint is already the right one; written on every pick. */
  const [viewMode, setViewMode] = useState<'list' | 'grid'>(() => {
    if (typeof window === 'undefined') return 'list';
    try {
      return window.localStorage.getItem(`pear.nb.view.${session()?.memberId ?? 'anon'}`) === 'grid'
        ? 'grid' : 'list';
    } catch { return 'list'; }
  });
  const pickView = useCallback((mode: 'list' | 'grid') => {
    setViewMode(mode);
    try {
      window.localStorage.setItem(`pear.nb.view.${session()?.memberId ?? 'anon'}`, mode);
    } catch { /* private mode — the pick still applies for this visit */ }
  }, []);
  /* PRD E3/E4 — THIS person's opinion of each group (dissolve / remove / add),
     kept exactly where the List/Grid pick keeps its own: their localStorage,
     under their member id. Nothing is sent anywhere, so one partner hiding a
     group never hides it for the other. */
  const [clusterPrefs, setClusterPrefs] = useState<ClusterPrefs>(() => readClusterPrefs(me?.memberId ?? null));
  const [openCluster, setOpenCluster] = useState<string | null>(null);
  const [clusterAddPick, setClusterAddPick] = useState('');
  const writeClusterPrefs = useCallback((next: ClusterPrefs) => {
    setClusterPrefs(next);
    try {
      window.localStorage.setItem(clusterPrefKey(session()?.memberId ?? null), JSON.stringify(next));
    } catch { /* private mode — the pick still applies for this visit */ }
  }, []);
  /* PRD P1 — Esc closes the open card. Window-level and in the capture phase
     so it works from anywhere on the surface, and it yields to any dialog
     already up (the More drawer, Settings, the tour — the shared Overlay
     primitive renders every one of them as role="dialog"): Escape belongs to
     whichever surface the user is actually looking at, never to both. */
  useEffect(() => {
    if (!contextId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="dialog"]')) return;
      setContextId(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [contextId]);

  /** The type the bar will save as: an explicit choice (chip, or the tab you
   *  picked) wins, otherwise the text decides (PRD §6.2 C2). */
  const linkTab = collection === 'all' || collection === 'link';
  const detectedType: EntryType = detectCaptureType(draft);
  /* An explicit choice always wins: the tab you are on, or the chip you
     picked. Detection only fills the gap, which is why the chip is editable —
     a pinned Memory stays a Memory even when the words carry a URL (the
     server files that URL through its link pipeline, as before). */
  const captureType: EntryType = quickType ?? detectedType;

  /** One pill click does both jobs: narrow the list to that collection and
   *  point the capture bar at that type. Everything / Link leave the bar on
   *  auto-detect — the bar is shared now, so 'Link' is not a separate form. */
  const pickPill = (key: 'all' | EntryType) => {
    setCollection(key);
    setQuickType(key === 'all' || key === 'link' ? null : key);
    setTypeMenuOpen(false);
    setQuickSaved(null);
    /* Picking a shelf is a capture intent (the chip narrows to that type), so
       a bar the person closed comes back rather than silently swallowing the
       pick (card ANrPXTBtF2B6). */
    if (!captureOpenRef.current) setCaptureOpen(true);
  };

  /** Escape ends a capture: text gone, type back to auto, caret out of the
   *  bar (PRD §6.2 C4 — "Esc clears"). Nothing is saved. */
  const clearCapture = useCallback(() => {
    setDraft('');
    setQuickType(null);
    setTypeMenuOpen(false);
    setQuickSaved(null);
    setSharedDraft('us'); // next entry defaults back to shared
    setGiftDraft('me'); // …and the next GIFT defaults back to just me
    (document.getElementById('nb-quick') as HTMLTextAreaElement | null)?.blur();
  }, []);

  /* Founder email 2026-10-21 (card ANrPXTBtF2B6) — the quick-entry composer
     can be CLOSED, on request, with one X. Card mG5eA32qztRg (Jonathan
     2026-10-24) flips the default: the bar now starts CLOSED, so the feed
     owns the screen and capture happens when it is wanted — tap the compact
     row, paste anywhere, or pick a shelf. Closing still discards nothing it
     should not: the X and Escape reset the draft through clearCapture, while
     a scroll collapse keeps it (see the effect below). */
  const [captureOpen, setCaptureOpenState] = useState(false);
  const captureOpenRef = useRef(false);
  /** When the bar was last opened — the scroll-collapse rule needs it, and the
      declaration has to sit above the setter that stamps it (mG5eA32qztRg). */
  const captureOpenedAt = useRef(0);
  const setCaptureOpen = useCallback((open: boolean) => {
    if (open) captureOpenedAt.current = Date.now();
    captureOpenRef.current = open;
    setCaptureOpenState(open);
  }, []);
  const openCapture = useCallback(() => {
    setCaptureOpen(true);
    window.setTimeout(() => {
      const el = document.getElementById('nb-quick') as HTMLTextAreaElement | null;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }, 0);
  }, [setCaptureOpen]);
  const closeCaptureNow = useCallback(() => {
    clearCapture();
    setCaptureOpen(false);
  }, [clearCapture, setCaptureOpen]);

  /* Card 9wlZ8NbjACIY — tap-AWAY dismissal: the sheet closes, the draft
     stays. A stray tap on the feed must never throw away words, which is the
     promise the compact bar already made (only the X and Escape clear). */
  const dismissCapture = useCallback(() => {
    setCaptureOpen(false);
  }, [setCaptureOpen]);

  /* Escape is still the CANCEL (PRD §6.2 C4, "Esc clears"), and it belongs to
     the sheet whoever holds focus. This listener is registered at MOUNT on
     purpose: child effects (the overlay primitive's own window listener)
     register first on every commit, so beating it to the node once is what
     makes the ordering stable. Two jobs — an inner surface that owns Escape
     (the (?) help bubble, the type menu) gets the key: the event is marked
     handled so the overlay does not ALSO close, and the inner handler still
     runs; otherwise this is the cancel: clear the draft, close the sheet.
     Guarded on the sheet really being rendered (the overlay slot is
     exclusive, so capture can be "open" while another dialog holds it). */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!captureOpenRef.current) return;
      if (!document.querySelector('[data-testid="nb-capture-sheet"]')) return;
      if (document.querySelector('.pear-tooltip-bubble, .nb-type-menu')) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      clearCapture();
      setCaptureOpen(false);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [clearCapture, setCaptureOpen]);

  /* Card 9wlZ8NbjACIY — the scroll collapse went with the inline bar: the
     sheet pops in OVER the feed without locking it (a wheel over the feed
     scrolls the feed, a wheel over the sheet scrolls the sheet), so there is
     no collapse left to fire. Dismissal is tap-away (draft kept) or X /
     Escape (draft cleared). */

  /** Founder email 2026-10-19: after an entry the bar RESETS ITSELF — field
   *  cleared, focus back in the field — so the next capture starts without a
   *  refresh and without hunting for a close button. */
  const resetQuickField = useCallback((prevBody: string) => {
    setDraft(cur => (cur.trim() === prevBody ? '' : cur));
    window.setTimeout(() => {
      const el = document.getElementById('nb-quick') as HTMLTextAreaElement | null;
      if (el && document.activeElement !== el) el.focus();
    }, 0);
  }, []);

  /** The bar stays open after a save; this is the inline confirmation
   *  and its 4s life. Re-saving inside that window replaces the timer rather
   *  than stacking a second one, so the line cannot vanish early. */
  const flashQuickSaved = useCallback((type: EntryType) => {
    setQuickSaved(type);
    window.clearTimeout(quickSavedTimer.current);
    quickSavedTimer.current = window.setTimeout(() => setQuickSaved(null), 4000);
  }, []);

  /** The saved-with-Undo line (PRD §6.2 C5). It names the home the entry went
   *  to and holds the row's id, so Undo removes exactly that row. */
  const flashCaptureToast = useCallback((itemId: string | null, text: string) => {
    setCaptureToast({ itemId, text });
    window.clearTimeout(captureToastTimer.current);
    captureToastTimer.current = window.setTimeout(() => setCaptureToast(null), 8000);
  }, []);

  const undoCapture = useCallback(async (itemId: string) => {
    try {
      await api(`/api/notebook/${itemId}`, { method: 'PUT', body: { removed: true } });
      setCaptureToast(null);
      setJustSavedId(cur => (cur === itemId ? null : cur));
      await reloadRef.current?.();
    } catch (err) {
      console.error('[Pear] capture undo failed', err);
    }
  }, []);

  // Escape clears the bar from wherever focus sits inside it (C4).
  const captureKeyDown = (e: ReactKeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    /* Escape is one of the two paths back to the compact row (mG5eA32qztRg):
       draft cleared by clearCapture, bar closed here. */
    clearCapture();
    setCaptureOpen(false);
  };

  /** The date pool is fetched on its own cadence: it belongs to a different
   *  endpoint (and, in prod, a different durable object) than the notebook,
   *  so a slow /api/dates must never take the notebook list down with it. */
  const loadDates = useCallback(async () => {
    try {
      const data = await api<{ ideas: DateIdeaRow[] }>('/api/dates');
      setDateIdeas(Array.isArray(data.ideas) ? data.ideas : []);
    } catch (e) {
      console.error('[Pear] date ideas load failed', e);
      setDateIdeas(prev => prev ?? []);
    }
  }, []);

  useEffect(() => {
    void loadDates();
  }, [loadDates]);

  // More-menu quick link: narrow to that collection without pinning a capture
  // type. Landing here is a fresh arrival, so the bar goes back to auto-detect
  // instead of keeping whatever type the previous tab had chosen
  // (card quWhYyrNiIbt — the chip is state, and a filter door must not leave
  // it naming a type the person did not pick on this visit).
  useEffect(() => {
    if (!requestedCollection) return;
    setCollection(requestedCollection as 'all' | EntryType);
    setQuickType(null);
    setTypeMenuOpen(false);
  }, [requestedCollection]);

  /* Items and tags are independent loads (sol review 2026-10-19, card
     xNGOA5rZz9LV phase 3, high): they used to ride one Promise.all, so a tag
     endpoint outage blanked a perfectly loaded notebook and the catch set the
     whole panel to its failed state. Each half now answers for itself — the
     list is the product, tags are decoration on top of it — and the
     newest-response guard keeps an older answer from overwriting a newer one
     (that is the stale-overlap path that resurrected a just-removed row). */
  const reload = useCallback(async () => {
    const seq = ++reloadSeq.current;
    const [itemsRes, tagsRes] = await Promise.allSettled([
      api<{ items: NotebookItem[] }>('/api/notebook'),
      api<{ tags: NotebookTag[]; suggestions: Suggestion[] }>('/api/notebook/tags'),
    ]);
    if (seq !== reloadSeq.current) return; // a newer reload already answered
    if (itemsRes.status === 'fulfilled') {
      setItems(itemsRes.value.items ?? []);
      setFailed(false);
    } else {
      console.error('[Pear] notebook load failed', itemsRes.reason);
      setItems([]);
      setFailed(true);
    }
    if (tagsRes.status === 'fulfilled') {
      setTags(tagsRes.value.tags ?? []);
      setSuggestions(tagsRes.value.suggestions ?? []);
    } else {
      // Keep whatever tags are already on screen: the cards render fine
      // without a fresh tag list, they just do not gain new ones.
      console.error('[Pear] notebook tags load failed', tagsRes.reason);
    }
  }, []);

  useEffect(() => {
    reloadRef.current = reload;
    void reload();
    // Unfurl finishes after the response lands — poll twice so a fresh paste
    // fills in without the partner having to pull-to-refresh.
    const t1 = window.setTimeout(() => { void reload(); }, 2500);
    const t2 = window.setTimeout(() => { void reload(); }, 6000);
    return () => { window.clearTimeout(t1); window.clearTimeout(t2); };
  }, [reload]);

  /* PRD §6.2 C4 — the bar is reachable and usable from the keyboard, and a
     link can be captured from anywhere on the page:

       N        focus the bar (only when no field already has the keystroke)
       Enter    save (the bar's Composer passes enterBehavior='submit';
                Shift+Enter still breaks the line)
       Esc      clear (captureKeyDown on the form)
       paste    a URL pasted OUTSIDE an input starts a capture there and then
  */
  useEffect(() => {
    const focusBar = () => {
      /* The bar can be closed (card ANrPXTBtF2B6): N — and a URL pasted
         anywhere on the page — opens it again rather than doing nothing. */
      if (!captureOpenRef.current) { openCapture(); return; }
      const el = document.getElementById('nb-quick') as HTMLTextAreaElement | null;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      const typing = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (typing) return;
      if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        focusBar();
      }
    };
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const text = e.clipboardData?.getData('text')?.trim() ?? '';
      if (!text || !/https?:\/\//i.test(text)) return;
      e.preventDefault();
      setDraft(text);
      setQuickType(null); // a pasted link decides its own type
      setQuickSaved(null);
      window.setTimeout(focusBar, 0);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('paste', onPaste);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('paste', onPaste);
    };
  }, [openCapture]);

  // Gift enrichment (card OviOTaNsNivQ): the search runs AFTER the save has
  // answered, so a fresh gift card sits at 'pending' until a refetch sees the
  // detail land. Same cadence as the unfurl polls above, capped at eight
  // passes so a stalled engine cannot turn into an infinite loop of GETs.
  const pendingGift = (items ?? []).some((i) => i.entryType === 'gift' && i.enrichStatus === 'pending');
  useEffect(() => {
    if (!pendingGift || enrichPasses.current >= 8) return;
    enrichPasses.current += 1;
    const t = window.setTimeout(() => { void reload(); }, 3500);
    return () => window.clearTimeout(t);
  }, [pendingGift, reload, items]);

  // First-move hint (Rl0vzkZ2hdAe): the paste-a-link line has served its turn
  // the moment there is anything in the notebook — remember it per couple.
  useEffect(() => {
    if (items && items.length > 0) markHintSeen('notebook');
  }, [items]);

  /* ── The one capture save (card quWhYyrNiIbt item 1, PRD §6.2) ───────────
     The bar replaced two composers, so there is ONE submit path and it
     dispatches on the bar's type:
       link   -> POST /api/notebook {url, description, shared}; the receipt
                 (card VPlGeOn7LJPI) opens on the row the server created
       date   -> POST /api/dates — the couple's date POOL, not a notebook
                 card (the single-pipeline decision, PRD §3)
       gift   -> a URL inside it takes the server's link pipeline with
                 gift:true; words-only saves as a plain gift entry
       others -> POST /api/notebook {entryType, body, shared}
     C6: the words are stored as typed — detection only chooses the type. */
  const captureSave = async (e: FormEvent) => {
    e.preventDefault();
    const raw = draft.trim();
    if (!raw || adding) return;
    const type = captureType;
    setAdding(true);
    setCaptureError(null);
    setQuickError(null);
    try {
      if (type === 'date') {
        await api('/api/dates', { body: { title: raw } });
        resetQuickField(raw);
        setDateSaved(true);
        window.setTimeout(() => setDateSaved(false), 4000);
        flashQuickSaved('date');
        flashCaptureToast(null, 'Saved to your date ideas — it comes up next time you plan.');
        await loadDates();
        /* Saved: the bar goes back to its compact row (mG5eA32qztRg). The
           saved-with-Undo line is the toast below the sticky block, so the
           confirmation and its Undo survive the collapse. */
        setCaptureOpen(false);
        return;
      }

      const url = urlInCapture(raw);
      const words = url ? raw.replace(url, ' ').replace(/\s+/g, ' ').trim().slice(0, 500) : '';
      /* Card ggTQe2KbB0A3 HIGH-3: read the family's OWN draft, so a gift
         sends 'me' by default instead of inheriting the note that came before
         it. */
      const shared = draftSharedFor(type) === 'us';

      if (type === 'link' || (type === 'gift' && url)) {
        const res = await api<{ item?: { itemId: string; kind?: string; scope?: 'shared' | 'just_me'; entryType?: EntryType } }>('/api/notebook', {
          body: url
            ? { url, shared, ...(type === 'gift' ? { gift: true } : {}), ...(words ? { description: words } : {}) }
            /* Defensive: a link-typed capture with no URL is words, and must
               never be posted as a url (the server would store the sentence). */
            : { body: raw, entryType: 'note', shared },
        });
        const it = res?.item;
        const filedAsLink = (it?.entryType ?? 'link') === 'link';
        if (it?.itemId) {
          if (filedAsLink) {
            /* The receipt (card VPlGeOn7LJPI) already carries Undo, so a
               link save does not also get the toast — one confirmation. */
            setReceipt({ itemId: it.itemId, kind: it.kind ?? 'other', scope: it.scope ?? 'shared', filing: (it.kind ?? 'other') === 'other', undone: false, busy: false, error: null, tier: null });
            void trackFiling(it.itemId);
            /* Card Mpah9C81s6Fn: a card the current filter hides reads as a
               lost save — a link saved from Gift/Note/… follows the card home
               (Everything and Link already show it, so they stay put). */
            if (collection !== 'all' && collection !== 'link') setCollection('link');
          } else {
            flashCaptureToast(it.itemId, type === 'gift' ? 'Saved to your gifts.' : 'Saved to your notebook.');
            setJustSavedId(it.itemId);
            window.setTimeout(() => setJustSavedId(cur => (cur === it.itemId ? null : cur)), 1800);
            setCollection(type === 'gift' ? 'gift' : 'all');
          }
        }
        resetQuickField(raw);
        setSharedDraft('us');
        setGiftDraft('me'); // a gift starts private again for the next save
        /* Saved: back to the compact row (mG5eA32qztRg); the receipt below
           carries the confirmation and its own Undo. */
        setCaptureOpen(false);
        await reload();
        window.setTimeout(() => { void reload(); }, 2500);
        return;
      }

      const res = await api<{ item?: NotebookItem; acknowledgement?: string }>('/api/notebook', {
        body: { entryType: type, body: raw, shared },
      });
      /* The server answers a gift save with the concierge's acknowledgement
         (card OviOTaNsNivQ) — assert the shared line, then let the pending
         poll below watch the detail land on the card. */
      if (type === 'gift') {
        enrichPasses.current = 0;
        if (res?.acknowledgement !== undefined && res.acknowledgement !== GIFT_ACK) {
          console.warn('[Pear] gift acknowledgement drifted from the shared line');
        }
      }
      /* Card Mpah9C81s6Fn: a bare URL saved as words is filed as a Link card
         server-side, and a card the current filter hides reads as a lost
         save — so follow it to the collection that holds it. */
      const item = res?.item;
      const reclassified = item?.entryType === 'link' && collection !== 'link';
      if (reclassified) setCollection('link');
      if (item?.itemId) {
        setJustSavedId(item.itemId);
        window.setTimeout(() => setJustSavedId(cur => (cur === item.itemId ? null : cur)), 1800);
        flashCaptureToast(item.itemId, `Saved to your ${ENTRY_PLURAL[type].toLowerCase()}.`);
      }
      resetQuickField(raw);
      if (!reclassified) flashQuickSaved(type);
      setSharedDraft('us');
      setGiftDraft('me'); // …and a gift typed after a note starts private
      /* Saved: back to the compact row (mG5eA32qztRg) — the Undo toast below
         the sticky block stays put, so one tap still takes the row back. */
      setCaptureOpen(false);
      await reload();
    } catch (err) {
      console.error('[Pear] notebook capture failed', err);
      /* Out loud, and the draft stays: a cleared field on a failed save reads
         as success and invites the duplicate press (card xNGOA5rZz9LV).
         The announcement lands in the slot the person was USING: a capture
         carrying a URL failed down the link pipeline, so it speaks where the
         paste happened (nb-link-error); plain words use the typed slot
         (nb-quick-error). One message renders either way. */
      const msg = "That didn't save — your text is still here. Try again.";
      if (urlInCapture(raw)) setCaptureError(msg);
      else setQuickError(msg);
    } finally {
      setAdding(false);
    }
  };

  /* The receipt half of the save flow (card VPlGeOn7LJPI): the paste answers
     before the unfurl and the classifier do, so the receipt is set from the row
     the server just created and then refines itself as the verdict lands. It
     gives up after ~12s and says so rather than spinning forever on a host that
     never answered. */
  const trackFiling = async (itemId: string) => {
    for (let i = 0; i < 6; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await api<{ items: NotebookItem[] }>('/api/notebook');
        const it = (res.items ?? []).find(x => x.itemId === itemId);
        if (it) {
          setReceipt(cur => (cur && cur.itemId === itemId
            ? { ...cur, kind: it.kind, scope: it.scope, tier: it.kindTier ?? cur.tier, filing: it.kind === 'other' && !it.categoryReason && !it.kindTier }
            : cur));
          /* Once the band lands the cascade has ANSWERED — an 'ask' verdict is
             final until a person chooses, so there is nothing left to poll for. */
          if (it.kind !== 'other' || it.categoryReason || it.kindTier) return;
        }
      } catch {
        /* keep the receipt as it stands — the row is already saved */
      }
    }
    setReceipt(cur => (cur && cur.itemId === itemId ? { ...cur, filing: false } : cur));
  };

  /** Every receipt mutation is aimed at ONE row and reports only on that row
   *  (sol review 2026-10-17): a paste made while a request is in flight installs
   *  a NEW receipt, and an unguarded updater would stamp "Removed" or the wrong
   *  audience onto it. The target id is captured up front, the buttons are
   *  disabled while the mutation runs, and a failure is shown in the receipt
   *  rather than only logged. */
  const mutateReceipt = async (target: string, run: () => Promise<void>, apply: (cur: NonNullable<typeof receipt>) => NonNullable<typeof receipt>) => {
    setReceipt(cur => (cur && cur.itemId === target ? { ...cur, busy: true, error: null } : cur));
    try {
      await run();
      setReceipt(cur => (cur && cur.itemId === target ? { ...apply(cur), busy: false, error: null } : cur));
      await reload();
    } catch (err) {
      console.error('[Pear] notebook receipt action failed', err);
      setReceipt(cur => (cur && cur.itemId === target
        ? { ...cur, busy: false, error: 'That didn\'t save — the link is unchanged. Try again.' }
        : cur));
    }
  };

  /** Undo the paste (PRD §6.1): remove the row outright — only its author can. */
  const undoReceipt = async () => {
    if (!receipt || receipt.undone || receipt.busy) return;
    const target = receipt.itemId;
    await mutateReceipt(
      target,
      async () => { await api(`/api/notebook/${target}`, { method: 'PUT', body: { removed: true } }); },
      cur => ({ ...cur, undone: true, filing: false }),
    );
  };

  /** Change the one thing the receipt can change safely: who sees it. */
  const flipReceiptScope = async () => {
    if (!receipt || receipt.undone || receipt.busy) return;
    const target = receipt.itemId;
    const next = receipt.scope !== 'shared';
    await mutateReceipt(
      target,
      async () => { await api(`/api/notebook/${target}`, { method: 'PUT', body: { shared: next } }); },
      cur => ({ ...cur, scope: next ? 'shared' : 'just_me' }),
    );
  };

  // Compose menu deep-link (card RvESgw7lVASX): a notebook tile lands here with
  // its type already chosen. The capture bar is shared, so the intent narrows
  // the collection AND points the bar's chip at that type (card quWhYyrNiIbt:
  // 'Link' has no separate form any more — the bar takes a URL itself and
  // auto-detects it).
  useEffect(() => {
    if (!intent) return;
    setCollection(intent.kind);
    setQuickType(intent.kind);
    setQuickSaved(null);
    /* A compose-menu deep link is a capture intent, so it reopens a bar the
       person had closed (card ANrPXTBtF2B6). */
    if (!captureOpenRef.current) setCaptureOpen(true);
    window.setTimeout(() => {
      (document.getElementById('nb-quick') as HTMLTextAreaElement | null)?.focus();
    }, 0);
  }, [intent, setCaptureOpen]);

  /* PWA share target (card jZn7vpkB7EG1): a share parked by main.tsx fills
     the bar and opens it. Words only — no fetch, no POST: Save is still the
     tap that writes to the couple's data. The payload is dropped as soon as
     it has been applied, so a tab away and back cannot re-fill a bar the
     person already dismissed. Type is pinned by shareCapture (a URL that
     parses → link, a url that will not → plain note, none → the bar's own
     detection). */
  useEffect(() => {
    if (!pendingShare) return;
    const { body, type } = shareCapture(pendingShare);
    if (body) {
      setDraft(body);
      setQuickType(type);
      setQuickSaved(null);
      if (!captureOpenRef.current) setCaptureOpen(true);
      window.setTimeout(() => {
        (document.getElementById('nb-quick') as HTMLTextAreaElement | null)?.focus();
      }, 0);
    }
    onShareConsumed?.();
  }, [pendingShare, onShareConsumed, setCaptureOpen]);

  /** A per-card failure, said on the card that failed and gone after 6s —
   *  long enough to read, short enough not to become furniture. */
  const flashRowError = useCallback((itemId: string, message: string) => {
    setRowError({ itemId, message });
    window.clearTimeout(rowErrorTimer.current);
    rowErrorTimer.current = window.setTimeout(() => setRowError(null), 6000);
  }, []);

  const toggleDone = async (item: NotebookItem) => {
    if (busyId) return;
    setBusyId(item.itemId);
    try {
      /* One canonical lifecycle field (sol review 2026-10-19, card
         xNGOA5rZz9LV): `status` is the facet the filter above reads, and the
         Durable Object keeps it and doneAt in step (markNotebookDone writes
         both in one UPDATE). Toggling off `status` means the verb on the card
         and the Done filter can never disagree about the same row. */
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { done: item.status !== 'done' } });
      setRowError(null);
      await reload();
    } catch (e) {
      console.error('[Pear] done toggle failed', e);
      // The card is still showing the OLD state, so the only lie would be
      // silence. Say it on the row that failed (card xNGOA5rZz9LV phase 3).
      flashRowError(item.itemId, 'Couldn\'t update that card — nothing changed. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  /** Notebook v2 typed entries are user-authored text — removing one should be
   *  possible (the card lives by choice, not by ingest). Deletes any entry.
   *
   *  Card xUZFWf8JtHo2 item 5 — quiet remove: no confirm modal. The row is
   *  removed on the spot and a transient "Removed — Undo" line appears, which
   *  re-adds it (PUT removed:false). Undo replaces the modal as the safety net,
   *  so removing stays one click and never blocks on a dialog. */
  const deleteEntry = async (item: NotebookItem) => {
    if (busyId) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { removed: true } });
      const label = item.entryType === 'link'
        ? (item.title?.trim() || item.url)
        : (item.body?.trim() || ENTRY_LABEL[item.entryType]);
      setRemovedItem({ item, label });
      window.clearTimeout(undoTimer.current);
      undoTimer.current = window.setTimeout(() => setRemovedItem(null), 8000);
      setRowError(null);
      await reload();
    } catch (e) {
      console.error('[Pear] notebook remove failed', e);
      // The card is still on screen (the PUT failed), so say so on the row.
      flashRowError(item.itemId, "Couldn't remove that — the card is still here. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  /* Card xUZFWf8JtHo2 item 5 — the other half of the quiet remove. Removal is a
     hard DELETE, so Undo re-creates the row from the fields captured at remove
     time: a typed entry re-posts its body + visibility, a link re-posts its URL.
     A re-created row gets a fresh itemId (and a re-unfurl for a link), which is
     the honest cost of a delete that is a real delete — the card comes back with
     its content, not its identity. */
  const undoRemove = async () => {
    if (!removedItem || busyId) return;
    const { item } = removedItem;
    setBusyId(item.itemId);
    try {
      const payload = item.entryType === 'link'
        ? { url: item.url, shared: item.shared }
        : { entryType: item.entryType, body: item.body ?? '', shared: item.shared };
      await api('/api/notebook', { body: payload });
      setRemovedItem(null);
      window.clearTimeout(undoTimer.current);
      setRowError(null);
      await reload();
    } catch (e) {
      console.error('[Pear] notebook undo failed', e);
      flashRowError(item.itemId, "Couldn't bring that card back — try removing it again to refresh.");
    } finally {
      setBusyId(null);
    }
  };

  /* Card Q4BluQX8fXgj — only the creator can remove an entry, even a shared
     "us" one (Jonathan, 2026-10-16). The server enforces it; this hides the
     control so a partner never sees a Remove button they can't use. */
  const canRemove = (item: NotebookItem) => me !== null && item.addedBy === me.memberId;

  /** Card 67zOnRgx4YTI item 1 — open the editor on one card. Links open with
   *  title + description; a typed entry opens with its own text. */
  const startEdit = (item: NotebookItem) => {
    setEditError(null);
    // A link whose unfurl never landed has no title yet — the card face shows
    // its host, so the editor opens with the URL rather than a blank field.
    setEditTitle(item.entryType === 'link' ? ((item.title ?? '').trim() || item.url) : '');
    setEditText(item.entryType === 'link' ? (item.description ?? '') : (item.body ?? ''));
    setEditingId(item.itemId);
    /* Card 2wn9seit8zGi — "Edit" fired from the context panel hands back to
       the card's own inline form (one editor, one source of truth), so bring
       that card into view: on desktop the panel sits beside the feed and the
       form would otherwise open off the top of the list. */
    window.requestAnimationFrame(() => {
      const card = document.querySelector(`[data-context-card="${item.itemId}"]`);
      card?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError(null);
  };

  /** Save the edit through the one PUT the server already enforces (owner
   *  only). A failed save keeps the draft on screen — same out-loud rule as
   *  the composers (card xNGOA5rZz9LV phase 3). */
  const saveEdit = async (item: NotebookItem) => {
    if (editBusy) return;
    setEditBusy(true);
    setEditError(null);
    try {
      const edit = item.entryType === 'link'
        ? { title: editTitle, description: editText }
        : { body: editText };
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { edit } });
      setEditingId(null);
      // Card ggTQe2KbB0A3 MEDIUM-3 — a rewritten gift restarts the pending
      // poll: the search runs after the response, so the budget must be
      // fresh or the refreshed detail never gets watched for.
      if (item.entryType === 'gift') enrichPasses.current = 0;
      await reload();
    } catch (err) {
      console.error('[Pear] notebook edit failed', err);
      setEditError('That edit didn\'t save — your text is unchanged. Try again.');
    } finally {
      setEditBusy(false);
    }
  };

  /** Card 2qBAZOM2LFWa — open the detail form for one card, seeded from what
   *  the row already holds. `event_date` is stored as epoch ms; the date
   *  input speaks `yyyy-mm-dd`, so both halves convert through UTC — the day
   *  a memory happened must not shift because of the timezone the reader is
   *  in. */
  const openDetail = (item: NotebookItem) => {
    setDetailId(item.itemId);
    setDetailDate(item.eventDate ? new Date(item.eventDate).toISOString().slice(0, 10) : '');
    setDetailPlace(item.place ?? '');
    setDetailError(null);
    setPlaceQuery('');
    setPlaceResults([]);
    setPlaceNote(null);
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetailError(null);
  };

  /** One search. The route meters it on the date planner's own daily ceiling
   *  and returns the refreshed usage, so the cost line is the truth and not
   *  an estimate held elsewhere. */
  const searchPlaces = async () => {
    const text = placeQuery.trim();
    if (!text || placeBusy) return;
    setPlaceBusy(true);
    setPlaceNote(null);
    try {
      const res = await api<{ places: PlacePick[]; capped: boolean; reason: string | null; usage: { estCostUsd: number } }>(
        '/api/notebook/places',
        { method: 'POST', body: { text } },
      );
      setPlaceResults(res.places ?? []);
      if (res.capped) {
        setPlaceNote("That's today's location search limit — the plain text box below still works.");
      } else if ((res.places ?? []).length === 0) {
        setPlaceNote("Nothing came back for that — type the place in yourself and it'll save fine.");
      }
    } catch (err) {
      console.error('[Pear] place search failed', err);
      setPlaceNote("Couldn't search just now — type the place in yourself.");
      setPlaceResults([]);
    } finally {
      setPlaceBusy(false);
    }
  };

  /** Save the day and the place. A cleared field is a real clear: sending
   *  `null` takes the value off the card rather than leaving it behind. */
  const saveDetail = async (item: NotebookItem) => {
    if (detailBusy) return;
    setDetailBusy(true);
    setDetailError(null);
    try {
      await api(`/api/notebook/${item.itemId}`, {
        method: 'PUT',
        body: {
          edit: {
            eventDate: detailDate ? Date.parse(`${detailDate}T12:00:00Z`) : null,
            place: detailPlace.trim() || null,
          },
        },
      });
      setDetailId(null);
      await reload();
    } catch (err) {
      console.error('[Pear] notebook detail save failed', err);
      setDetailError("That detail didn't save — what's on the card is unchanged. Try again.");
    } finally {
      setDetailBusy(false);
    }
  };

  /** PRD E2 — the panel's correction writes through the SAME edit verb the
   *  card's detail form uses (one save path for `place`), then the feed
   *  re-reads its rows so the chip updates where the person is looking. */
  const savePlace = async (item: NotebookItem, place: string) => {
    await api(`/api/notebook/${item.itemId}`, {
      method: 'PUT',
      body: { edit: { place: place.trim() || null } },
    });
    await reload();
  };

  /* ── PRD E4 — the cluster's combined actions ────────────────────────────────
     Plan this writes the group's own label into the date pool through the SAME
     POST the capture bar's "date" type uses (one route, one toast); Pick dates
     takes the person to the pool it just wrote; View on map is a plain search
     link — no key, no API, no spend. Dissolve / remove / add only rewrite this
     person's clusterPrefs. */

  const planCluster = async (c: FeedCluster) => {
    try {
      await api('/api/dates', { body: { title: c.label } });
      setDateSaved(true);
      window.setTimeout(() => setDateSaved(false), 4000);
      await loadDates();
    } catch (e) {
      console.error('[Pear] cluster plan failed', e);
    }
  };

  /* "Pick dates" lands on Us > Dates through the app's own destination
     handler: push the hash navigateTo would push, then hand it the popstate
     the Back/Forward retrace listens on (card Mn55eOvFgvC0), so the tab, the
     hash and the browser's own history all agree on where the person is. */
  const pickClusterDates = () => {
    window.history.pushState(null, '', '#us-dates');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const dissolveCluster = (key: string) => {
    setOpenCluster(null);
    setClusterAddPick('');
    writeClusterPrefs({ ...clusterPrefs, dissolved: [...new Set([...clusterPrefs.dissolved, key])] });
  };

  const removeClusterMember = (key: string, itemId: string) => {
    const excl = clusterExclKey(key, itemId);
    writeClusterPrefs({
      ...clusterPrefs,
      excluded: clusterPrefs.excluded.includes(excl)
        ? clusterPrefs.excluded
        : [...clusterPrefs.excluded, excl],
      added: Object.fromEntries(
        Object.entries(clusterPrefs.added).map(([k, v]) => [k, v.filter(id => id !== itemId)]),
      ),
    });
  };

  const addClusterMember = (key: string, itemId: string) => {
    const excl = clusterExclKey(key, itemId);
    const cur = clusterPrefs.added[key] ?? [];
    writeClusterPrefs({
      ...clusterPrefs,
      excluded: clusterPrefs.excluded.filter(e => e !== excl),
      added: { ...clusterPrefs.added, [key]: cur.includes(itemId) ? cur : [...cur, itemId] },
    });
    setClusterAddPick('');
  };

  /** E4's escape hatch: dissolve, remove and add are all opinions, so all of
   *  them come back with one control — no group can be lost for good. */
  const restoreClusters = () => {
    writeClusterPrefs(EMPTY_CLUSTER_PREFS);
    setClusterAddPick('');
  };

  /** Card fyOZFYwAtbZy (PRD §8) — a just-me card becomes shared only as a
   *  deliberate action: confirm first, then the owner-only PUT flips it.
   *  The server enforces ownership (a partner's private row is 404, a shared
   *  row that isn't theirs is 403); this only shows the control to the owner
   *  of a private card, so nobody gets a button that can never work. */
  const shareEntry = async (item: NotebookItem) => {
    if (busyId) return;
    if (!window.confirm('Share this with your partner? It will move into the shared notebook where both of you can see it.')) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { shared: true } });
      await reload();
    } catch (e) {
      console.error('[Pear] notebook share failed', e);
      window.alert('Could not share that — try again.');
    } finally {
      setBusyId(null);
    }
  };

  /** Card fyOZFYwAtbZy (sol review 2026-10-16) — sharing has to be reversible
   *  or a mistaken share can only be undone by deleting the card. Owner-only,
   *  same PUT with shared:false. Offered on typed entries AND link cards now
   *  (card xNGOA5rZz9LV phase 3): a private link used to keep only a badge,
   *  with no way back to shared and no way in from shared. */
  const makePrivate = async (item: NotebookItem) => {
    if (busyId) return;
    if (!window.confirm('Make this just-me again? Your partner may already have seen it — from now on it stays out of their notebook.')) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}`, { method: 'PUT', body: { shared: false } });
      await reload();
    } catch (e) {
      console.error('[Pear] notebook make-private failed', e);
      window.alert('Could not make that just-me — try again.');
    } finally {
      setBusyId(null);
    }
  };

  /** The chooser's write (card xNGOA5rZz9LV phase 3, sol review 2026-10-19):
   *  an 'ask' card carries the ranked candidates plus every kind, so a person
   *  can file it into Events, Places, Things, Trips — the homes the legacy
   *  correction dropdown could not reach. 'Filed by hand' is the honest default
   *  why, same convention as retag(); the server clears the band on success, so
   *  the chooser leaves with the question it answered. */
  const fileKind = async (item: NotebookItem, kind: string, subtype: string | null, label: string) => {
    if (busyId) return;
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}/kind`, { body: { kind, subtype, reason: `Filed by hand — ${label}` } });
      setRowError(null);
      await reload();
    } catch (e) {
      console.error('[Pear] kind filing failed', e);
      flashRowError(item.itemId, 'That filing didn\'t save — the card is unchanged. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const retag = async (item: NotebookItem, category: Category) => {
    if (busyId) return;
    const answer = window.prompt(`Why "${CATEGORY_LABEL[category]}"? (shown on the card)`, item.categoryReason || '');
    if (answer === null) return;
    /* Card vPoiTtAn26lI: manual categorisation is the ESSENTIALS route — agent
       filing is Plus, so a free couple files their own items. The reason is
       therefore optional on a hand pick (the route still requires one, by
       contract): an empty answer becomes an honest default instead of an alert
       that blocks the only route a free couple has. */
    const reason = answer.trim() || 'Filed by hand';
    setBusyId(item.itemId);
    try {
      await api(`/api/notebook/${item.itemId}/category`, { body: { category, reason } });
      await reload();
    } catch (e) {
      console.error('[Pear] retag failed', e);
      window.alert('That tag could not be saved — try again.');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Accept the agent's word, or override it with your own. Either way the
   * decision is written to the same read log the AI screen shows (card
   * ioKcDXGeHnE7) — the UI just posts it and reloads.
   */
  const resolveSuggestion = async (id: string, action: 'accept' | 'override', tag?: string) => {
    if (busySugg) return;
    setBusySugg(id);
    try {
      await api(`/api/notebook/suggestions/${id}`, {
        body: tag ? { action, tag } : { action },
      });
      setOverriding(null);
      setOverrideText('');
      await reload();
    } catch (e) {
      console.error('[Pear] suggestion resolve failed', e);
      window.alert('That suggestion could not be saved — try again.');
    } finally {
      setBusySugg(null);
    }
  };

  const sendDate = async (ideaId: string) => {
    if (busyId) return;
    setBusyId(ideaId);
    try {
      // Promote is the same action Us → Dates calls "Send to partner": the
      // idea leaves POOL and enters the decision loop as SUGGESTED.
      await api(`/api/dates/${ideaId}/promote`, { body: {} });
      await loadDates();
    } catch (e) {
      console.error('[Pear] send date idea failed', e);
    } finally {
      setBusyId(null);
    }
  };

  /* ── The links view (card LcJbm9wsWa0B, PRD §5 layer 1/2 + §6.7) ──────────
   * The category dropdown is the top-level index and its order is the founder's
   * (NOTEBOOK_LINK_KINDS), so the options render straight off the routing table
   * rather than off a hand-kept list that could drift. Subtype tabs appear only
   * once a kind is chosen; the status control and search narrow the same set.
   * Card vHyQrgOMJyPg: the chip ROW is gone — one search, one Filters button
   * whose panel carries every category, subtype and lifecycle control. */
  const [kindFilter, setKindFilter] = useState<string | 'all'>('all');
  const [subtypeFilter, setSubtypeFilter] = useState<string | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'saved' | 'planned' | 'done'>('all');
  const [search, setSearch] = useState('');
  /* Sol review 2026-10-19 (card xNGOA5rZz9LV): the subtype and lifecycle
     controls moved behind one Filters button so a default visit shows the
     cards, not the furniture. The count on the button is the number of things
     currently narrowing the list, so a filter that narrows silently is
     impossible — and Clear all puts the view back in one click. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  /* Sol review 2026-10-19, card xNGOA5rZz9LV phase 2 — the calm card face.
     Per-card Details disclosure, plus a separate opener for the suggestions so
     a pending scan still announces itself on the face ("N suggested tags")
     without the editors landing on every visit. Both key on itemId, so one
     card open never opens its neighbours. */
  const [detailsOpen, setDetailsOpen] = useState<ReadonlySet<string>>(new Set());
  const [suggsOpen, setSuggsOpen] = useState<ReadonlySet<string>>(new Set());
  const toggleId = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const activeFilterCount =
    (kindFilter !== 'all' ? 1 : 0) +
    (subtypeFilter !== 'all' ? 1 : 0) +
    (statusFilter !== 'all' ? 1 : 0) +
    (search.trim() ? 1 : 0);
  const clearFilters = () => {
    setKindFilter('all');
    setSubtypeFilter('all');
    setStatusFilter('all');
    setSearch('');
  };

  /* ── PRD §8 keyboard: the feed is one tab stop, arrows walk it ─────────────
     Arrow Down/Up (Left/Right too, for grid view) move between cards, Enter
     opens the focused card in the context panel and Escape closes it. The
     handler bails unless the CARD itself is the event target, so the caret
     still walks text in the edit composer, the selects still step their values
     and the capture bar's own Escape (C4) keeps meaning "clear the field". */
  const feedKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    const card = target.closest<HTMLElement>('[data-context-card]');
    if (!card || target !== card) return;
    const cards = [...e.currentTarget.querySelectorAll<HTMLElement>('[data-context-card]')];
    const i = cards.indexOf(card);
    if (i < 0) return;
    const moveTo = (next: number) => {
      const el = cards[next];
      if (!el) return;
      e.preventDefault();
      setFeedRoving(el.dataset.contextCard ?? null);
      el.focus();
    };
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') moveTo(i + 1);
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') moveTo(i - 1);
    else if (e.key === 'Enter') {
      if (!contextLive) return;
      const id = card.dataset.contextCard ?? null;
      if (!id) return;
      e.preventDefault();
      setContextId(cur => (cur === id ? null : id));
    } else if (e.key === 'Escape') {
      if (!contextId) return;
      e.preventDefault();
      setContextId(null);
    }
  };

  const linkItems = (items ?? []).filter(i => i.entryType === 'link');

  /* Card xUZFWf8JtHo2 item 2 — the category counts (the dropdown's option
     labels, formerly the chip index) count the links the current
     search / status / subtype leave on the table, so the numbers agree with the
     cards actually shown (before this they read the raw link total, so a
     narrowing left stale counts). The KIND filter itself is excluded on
     purpose: choosing one kind must never hide the other categories behind a
     count of 0. */
  const chipLinks = (items ?? []).filter(i => {
    if (i.entryType !== 'link') return false;
    if (subtypeFilter !== 'all' && (i.subtype ?? '') !== subtypeFilter) return false;
    if (statusFilter !== 'all' && (i.status ?? 'saved') !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = [i.title ?? '', i.url, i.description ?? '', i.place ?? ''].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  /** Every kind that has at least one card, in taxonomy order — the rows of
   *  the category dropdown. The row STAYS as long as the kind exists in the
   *  full link set (the dropdown is the only way to switch kinds now, so a
   *  status/subtype/search filter must never make it vanish) — but its COUNT
   *  reflects the narrowed set, so a kind with nothing matching reads an
   *  honest 0. */
  const kindChips = NOTEBOOK_LINK_KINDS
    .map(kind => ({
      kind,
      n: chipLinks.filter(i => i.kind === kind).length,
      total: linkItems.filter(i => i.kind === kind).length,
    }))
    .filter(c => c.total > 0);

  /** Subtypes actually present under the chosen kind (open-ended: anything the
   *  classifier or the user wrote is shown, named kinds first). */
  const subtypeTabs = kindFilter === 'all'
    ? []
    : [...new Set([
        ...(NOTEBOOK_LINK_SUBTYPES[kindFilter as keyof typeof NOTEBOOK_LINK_SUBTYPES] ?? []),
        ...linkItems.filter(i => i.kind === kindFilter && i.subtype).map(i => i.subtype as string),
      ])].filter(s => linkItems.some(i => i.kind === kindFilter && i.subtype === s));

  const visible = (items ?? []).filter(i => {
    if (collection !== 'all' && i.entryType !== collection) return false;
    /* The links view's narrowing applies to LINKS only (sol review 2026-10-17):
       a typed entry has no kind, subtype or status to answer to, and its filter
       controls are hidden on its own tab — so a category choice or a search left
       over from the Links view must never empty Memories, Notes or Ideas. The exemption is
       on the ENTRY TYPE, not on the collection, because switching tabs keeps
       these controls' state.

       Card xUZFWf8JtHo2 item 3 — the one narrowing that DOES reach typed cards
       is the search, and only on Everything (where the placeholder now says so).
       On a single-collection tab the search stays a link filter, so a leftover
       link query can never empty Memories, Notes or Ideas. */
    if (i.entryType !== 'link') {
      if (collection !== 'all') return true;
      const tq = search.trim().toLowerCase();
      if (!tq) return true;
      const tyhay = [i.body ?? '', i.title ?? '', i.place ?? ''].join(' ').toLowerCase();
      return tyhay.includes(tq);
    }
    if (kindFilter !== 'all' && i.kind !== kindFilter) return false;
    if (subtypeFilter !== 'all' && (i.subtype ?? '') !== subtypeFilter) return false;
    if (statusFilter !== 'all' && (i.status ?? 'saved') !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = [i.title ?? '', i.url, i.description ?? '', i.place ?? ''].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  /* PRD E3/E4 — the groups themselves. Rendered only where the panel a member
     row opens actually exists (>=760px) and only on the Everything tab: below
     that hinge the phone keeps the plain list it has always had (PRD L5), and
     a group under a type tab would just repeat what the tab already says. */
  const clusters = collection === 'all' && contextLive
    ? deriveClusters(visible, tags, clusterPrefs)
    : [];
  const clusterPrefsDirty = clusterPrefs.dissolved.length > 0
    || clusterPrefs.excluded.length > 0
    || Object.values(clusterPrefs.added).some(v => v.length > 0);

  /* PRD §8 — exactly ONE card is a tab stop at a time (roving tabindex), so
     Tab enters the feed once instead of stopping at every card while the
     Arrow keys still walk the whole list. The stop falls back to the first
     visible card when the roving card was filtered away or never focused. */
  const rovingCardId =
    feedRoving && visible.some(v => v.itemId === feedRoving)
      ? feedRoving
      : (visible[0]?.itemId ?? null);

  /* Card xUZFWf8JtHo2 item 1 — the counter must answer "how much of what this
     tab holds is on screen?", so its base follows the tab: every card on
     Everything, links on the Link tab. It used to count link cards against the
     link total on BOTH tabs, so Everything read "1 of 1" above three cards. The
     numerator is always the rendered count. */
  const counterBase = collection === 'link' ? linkItems.length : (items ?? []).length;

  /* Card 2wn9seit8zGi (PRD F5) — the List / Grid switch, rendered inside the
     links bar when that bar exists and on its own line when it does not (a
     typed tab has no links bar). One element, two placements. */
  const viewToggle = contextLive && items !== null && items.length > 0 ? (
    <div className="nb-view" role="group" aria-label="How to show the cards">
      <button
        type="button"
        className={`nb-view-btn${viewMode === 'list' ? ' is-on' : ''}`}
        aria-pressed={viewMode === 'list'}
        data-test="nb-view-list"
        onClick={() => pickView('list')}
      >
        List
      </button>
      <button
        type="button"
        className={`nb-view-btn${viewMode === 'grid' ? ' is-on' : ''}`}
        aria-pressed={viewMode === 'grid'}
        data-test="nb-view-grid"
        onClick={() => pickView('grid')}
      >
        Grid
      </button>
    </div>
  ) : null;

  return (
    <div className="nb-panel">
      {/* Card 2wn9seit8zGi (PRD L1-L4) — the feed is one zone and the context
          panel the other. Below 1100px `.nb-feed` is just a plain block and
          the panel is a slide-over; at >=1100px the pair becomes the
          three-zone shell (rail | feed | panel) the PRD asks for. */}
      <div className="nb-feed">
      <h2 className="nb-title">Notebook</h2>
      <p className="nb-lead">Save links, gifts, memories, dates — anything you both want to keep.</p>
      {/* First-run hint for the Archivist persona — names the one action that
          starts this tab: paste a link (TFeLwOAS1EeP). Rl0vzkZ2hdAe: it stops
          rendering once the notebook holds anything. Card 7VIbSBeJhvbQ gates
          it to the tabs that own the link action, so a typed tab (Gift /
          Memory / Date / Note / Idea) no longer tells you to paste a link. */}
      {linkTab && (
        <TabHint id="hint-notebook" hintKey="notebook">
          First move: paste a link you both want to keep — it lands here as a card you can both open.
        </TabHint>
      )}

      {/* ── Content-type nav — TABS at the top of the surface (card cMgatOnZPBZU).
          Same component every screen with a sub-nav uses: real tab semantics,
          horizontal scroll (a wrapped row reads as pills again), active tab
          emphasiSed with the Velvet Room mustard fill. It still picks the
          quick-add composer AND narrows the list. */}
      {/* ── The capture bar (card quWhYyrNiIbt item 1, PRD §6.2 C1-C5) ─────
          ONE sticky bar at the top of the feed for every type. The two
          composers it replaces (a typed quick-add and a link paste form) are
          gone: the type is a CHIP the text decides and the person can
          override, and who-can-see is a two-state control instead of the old
          Gift toggle. The ids the walks target (nb-quick / nb-quick-input /
          nb-quick-add) are unchanged, so the suite still drives the surface. */}
      {/* The bar and the collection tabs are ONE sticky block (PRD §7: "capture
          bar and tabs sticky"). Two independently sticky rows at top:0 would
          pin on top of each other, so the wrapper owns the pin and the tab row
          stops being sticky on its own. */}
      <div className="nb-sticky-top">
      {/* Card 9wlZ8NbjACIY (Jonathan 2026-10-24) — the EXPANDED state is a
          pop-in SHEET, not an inline stretch of the sticky bar: the compact
          row stays exactly as it is, one tap pops the full form in over the
          feed, and the whole element UI (field, type chip + menu, help tip,
          who-can-see, Save, error/saved lines) gets the panel to itself.

          It is a POP-IN, not a modal: the backdrop is transparent and
          click-through, so the feed behind stays readable and scrollable and
          the sheet does not ambush a tap meant for a card or a shelf tab.
          Tapping away dismisses it with the draft intact (only the X and
          Escape throw words away), the same promise the compact bar has.
          Same ids, same save path, same X and Escape — only the container
          moved, into the app's one overlay primitive. */}
      <Overlay
        open={captureOpen}
        onClose={dismissCapture}
        className="nb-capture-overlay"
        panelClassName="more-sheet nb-capture-sheet"
        panelTestId="nb-capture-sheet"
        label="Save a link, idea, gift or memory"
        initialFocus="#nb-quick"
        trap={false}
        inertBackground={false}
        lockScroll={false}
        dismissOnBackdrop={false}
        closeOnOutsidePointer
      >
      <form className="nb-capture" data-testid="nb-capture" onSubmit={captureSave} onKeyDown={captureKeyDown}>
        <Composer
          id="nb-quick"
          bare
          dataTest="nb-quick-input"
          inputClassName="pear-chat-input nb-input nb-capture-input"
          placeholder="Save a link, idea, gift or memory…"
          ariaLabel={`Capture a ${ENTRY_LABEL[captureType].toLowerCase()}`}
          value={draft}
          enterBehavior="submit"
          onChange={v => { setDraft(v); setQuickSaved(null); }}
        />
        <div className="nb-capture-row">
          {/* C2 — the detected type, editable. A URL always reads as a link
              (a link cannot be words); everything else can be re-typed. */}
          <div className="nb-chip-wrap">
            <button
              type="button"
              className={`nb-type-chip nb-type-${captureType}`}
              data-test="nb-type-chip"
              aria-haspopup="menu"
              aria-expanded={typeMenuOpen}
              aria-label={`Saving as ${ENTRY_LABEL[captureType]} — change the type`}
              onClick={() => setTypeMenuOpen(o => !o)}
            >
              <span className="nb-icon" aria-hidden="true">{ENTRY_ICON[captureType]}</span>
              {ENTRY_LABEL[captureType]}
              <span className="nb-chip-caret" aria-hidden="true">▾</span>
            </button>
            {typeMenuOpen && (
              <div className="nb-type-menu" role="menu" aria-label="Save as">
                {CAPTURE_TYPES.map(t => (
                  <button
                    key={t}
                    type="button"
                    role="menuitemradio"
                    aria-checked={captureType === t}
                    className={`nb-type-opt${captureType === t ? ' is-on' : ''}`}
                    data-test={`nb-type-${t}`}
                    onClick={() => { setQuickType(t); setTypeMenuOpen(false); }}
                  >
                    <span className="nb-icon" aria-hidden="true">{ENTRY_ICON[t]}</span>
                    {ENTRY_LABEL[t]}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* mG5eA32qztRg (Jonathan 2026-10-24) — the hint paragraph that
              duplicated the placeholder under the bar is GONE. What it carried
              that a placeholder cannot (the four types, that Pear files it for
              you, who each visibility setting shares it with) lives one tap
              away, beside the chip that picks the type. */}
          <Tooltip label="Save a link, an idea, a gift or a memory — Pear files it for you. Shared means your partner sees it too; 'Just you' keeps it off their list.">
            <span className="pear-tip-trigger" title="Save a link, an idea, a gift or a memory — Pear files it for you. Shared means your partner sees it too; 'Just you' keeps it off their list.">ⓘ</span>
          </Tooltip>
          {/* mG5eA32qztRg — one right-aligned cluster: the who-can-see control
              and Save share the same right edge, so 'Just you' lines up with
              Save instead of floating between the chip and the margin. */}
          <div className="nb-capture-actions">
          {/* C3 — Shared / Just me, default Shared (card sYwPmYEIf4VC). A date
              idea has no private version: it enters the couple's own planning
              pipeline. A GIFT defaults to Just me and keeps its OWN switch
              (card ggTQe2KbB0A3 HIGH-3), so a surprise cannot be widened by a
              note saved a moment earlier. */}
          {captureType !== 'date' && (
            <div className="nb-visibility" role="radiogroup" aria-label="Who can see this entry">
              <button
                type="button"
                role="radio"
                id="nb-vis-us"
                aria-checked={draftSharedFor(captureType) === 'us'}
                className={`nb-vis-btn${draftSharedFor(captureType) === 'us' ? ' is-on' : ''}`}
                onClick={() => (captureType === 'gift' ? setGiftDraft('us') : setSharedDraft('us'))}
              >
                Shared
              </button>
              <button
                type="button"
                role="radio"
                id="nb-vis-me"
                aria-checked={draftSharedFor(captureType) === 'me'}
                className={`nb-vis-btn${draftSharedFor(captureType) === 'me' ? ' is-on' : ''}`}
                onClick={() => (captureType === 'gift' ? setGiftDraft('me') : setSharedDraft('me'))}
              >
                Just you
              </button>
            </div>
          )}
          {/* PRD §6.7 D7 — gold is the ACTION role, and Save is the one action
              on this surface, so it is the only mustard fill here. */}
          <button
            type="submit"
            data-test="nb-quick-add"
            className="pear-btn pear-btn-mustard nb-add nb-capture-save"
            disabled={adding || draft.trim().length === 0}
          >
            {adding ? 'Saving…' : 'Save'}
          </button>
          </div>
          {/* The one dismiss path the founder asked for (email 2026-10-21).
              type="button" so it can never submit, and it discards the draft
              through the same reset Escape uses — no row is written. Stays
              outside the Save cluster: the dismiss is not part of the action
              group (mG5eA32qztRg). */}
          <button
            type="button"
            className="nb-capture-close"
            data-test="nb-capture-close"
            aria-label="Close the capture bar"
            onClick={closeCaptureNow}
          >
            <svg {...ICON_ATTRS}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </div>
        {/* mG5eA32qztRg — the nb-capture-hint paragraph is gone: it said what
            the placeholder already says. Its per-type/visibility copy lives in
            the (?) tooltip beside the chip above. */}
        {quickError && (
          <p className="nb-save-error" data-test="nb-quick-error" role="alert">{quickError}</p>
        )}
        {captureError && (
          <p className="nb-save-error" data-test="nb-link-error" role="alert">{captureError}</p>
        )}
        {quickSaved && (
          <p className="nb-saved" data-test="nb-saved" role="status">
            Saved to your {ENTRY_PLURAL[quickSaved].toLowerCase()} — ready for the next one.
          </p>
        )}
      </form>
      </Overlay>
        {/* Compact (card mG5eA32qztRg): the closed bar reads as a FIELD, not a
           note to self — the placeholder text Jonathan's eye expects, with the
           mustard + at the right edge as the one action. Same 44px floor, same
           slot, so the sticky block never jumps between the two states. It is
           the only thing left in this block: the form above it lives in the
           sheet (card 9wlZ8NbjACIY), so it stays put while the sheet is up. */}
        <button
          type="button"
          className="nb-capture-reopen"
          data-test="nb-capture-reopen"
          aria-label="Save a link, idea, gift or memory — open the capture bar"
          onClick={openCapture}
        >
          <span className="nb-capture-reopen-text">Save a link, idea, gift or memory…</span>
          <span className="nb-capture-plus" aria-hidden="true">+</span>
        </button>

      <SubTabs
        name="nb-collection"
        label="Notebook collection"
        tabs={ENTRY_TILES.map(t => ({ key: t.key, label: t.label }))}
        active={collection}
        onChange={key => pickPill(key as 'all' | EntryType)}
        idFor={nbSubTabId}
      />
      </div>
      <SubPanel name="nb-collection" tabKey={collection} idFor={nbSubTabId}>

        {/* The receipt (card VPlGeOn7LJPI, PRD §6.1): where the paste filed, who
            can see it, one honest change, and the undo a mis-paste needs. It
            lives under the composer, not in a floating toast, because the
            composer is where the paste happened and the next one happens. */}
        {receipt && (
          <div className="nb-receipt" data-test="nb-receipt" role="status">
            <p className="nb-receipt-text" data-test="nb-receipt-text">
              {receipt.undone
                ? 'Removed — that link is gone.'
                : receipt.filing
                  ? 'Saved. Still reading the link to file it…'
                  /* Card xNGOA5rZz9LV phase 3: the band is part of the receipt
                     now. An unsure verdict used to read exactly like a
                     confident one — same "Filed to …" sentence — so the one
                     case that needs a person looked like the case that did not. */
                  : receipt.tier === 'ask'
                    ? `Saved — nothing confident filed this one · ${receipt.scope === 'shared' ? 'Shared with them' : 'Just you'}. Pick its home on the card below.`
                    : receipt.tier === 'check'
                      ? `Filed to ${notebookRouteFor(receipt.kind).homeLabel} · worth a check · ${receipt.scope === 'shared' ? 'Shared with them' : 'Just you'}.`
                      : `Filed to ${notebookRouteFor(receipt.kind).homeLabel} · ${receipt.scope === 'shared' ? 'Shared with them' : 'Just you'}.`}
            </p>
            {!receipt.undone && (
              <div className="nb-receipt-actions">
                <button
                  type="button"
                  data-test="nb-receipt-change"
                  className="pear-btn pear-btn-quiet nb-receipt-btn"
                  disabled={receipt.filing || receipt.busy}
                  onClick={() => void flipReceiptScope()}
                >
                  {receipt.scope === 'shared' ? 'Make it just you' : 'Share it with them'}
                </button>
                <button
                  type="button"
                  data-test="nb-receipt-undo"
                  className="pear-btn pear-btn-quiet nb-receipt-btn"
                  disabled={receipt.busy}
                  onClick={() => void undoReceipt()}
                >
                  {receipt.busy ? 'Working…' : 'Undo'}
                </button>
              </div>
            )}
            {receipt.error && (
              <p className="nb-receipt-error" data-test="nb-receipt-error" role="alert">{receipt.error}</p>
            )}
            <button
              type="button"
              data-test="nb-receipt-dismiss"
              className="nb-receipt-x"
              aria-label="Dismiss this receipt"
              onClick={() => setReceipt(null)}
            >
              ✕
            </button>
          </div>
        )}

        {/* Gifting concierge strip — shown only on the Gift tab (cards
            JKXJy2U9FVwx / wYPNXGVzAb63 / Mk8sHH7lfFDM / jqPcHtCfaE7K). */}
        {collection === 'gift' && <GiftingAgent />}

        {/* The Vault half of the Date Planner (card ZdYBVP203b8a, PRD §6.1).
            A date idea saved on this tile lands in the couple's POOL — the
            same DateIdea Us → Dates plans from — so the tile shows where it
            went, with the provenance line the PRD asks for and the one action
            that moves an idea into the decision loop. */}
        {collection === 'date' && (
          <section className="nb-dates" id="nb-dates" aria-label="Your date ideas">
            <h3 className="nb-dates-head">Your date ideas</h3>
            {dateIdeas === null ? (
              <SkeletonBlock lines={2} />
            ) : dateIdeas.every(d => d.state !== 'pool') ? (
              <p className="nb-empty" id="nb-dates-empty">
                Nothing waiting — save one above and it comes up the next time you plan together.
              </p>
            ) : (
              <ul className="nb-dates-list" id="nb-dates-pool">
                {dateIdeas.filter(d => d.state === 'pool').map(d => (
                  <li className="nb-date-row" key={d.ideaId} data-idea={d.ideaId}>
                    <div className="nb-date-main">
                      <p className="nb-date-title">{d.title}</p>
                      {d.note && <p className="nb-date-note">{d.note}</p>}
                      <p className="nb-date-meta">
                        {dateMeta(d)}{d.provenance ? ` · ${d.provenance}` : ''}
                      </p>
                    </div>
                    <Pressable
                      className="pear-btn pear-btn-blush nb-date-send"
                      disabled={busyId === d.ideaId}
                      onClick={() => void sendDate(d.ideaId)}
                    >
                      Send to partner
                    </Pressable>
                  </li>
                ))}
              </ul>
            )}
            <p className="nb-dates-foot">Planning happens in Us → Dates — this is where ideas wait.</p>
          </section>
        )}

        {/* ── The links view (card LcJbm9wsWa0B, PRD §5/§6.7) ────────────────
            Founder email 2026-10-19: the filter stack was busy — chips, tabs,
            pills and search over four rows. Sol review 2026-10-19 (card
            xNGOA5rZz9LV) went further: the cards, not the controls, should own
            the first screen. So the default visit is now ONE row — search, a
            Filters button carrying the live count of what is narrowing, and
            the "N of M" the narrowings leave. Card vHyQrgOMJyPg finished the
            job the founder asked for on 2026-10-22: the chip row is gone
            entirely, and Filters carries ONE category dropdown (every kind,
            counts in the option labels) alongside subtype and lifecycle — so
            there is one search and one filter door, not a row of pills. */}
        {linkTab && linkItems.length > 0 && (
          <div className="nb-links-view" data-test="nb-links-view">
            <div className="nb-links-bar">
              <input
                type="search"
                data-test="nb-search"
                className="nb-search"
                /* Card xUZFWf8JtHo2 item 3 — the placeholder names what this tab
                   actually holds. It used to read "Search your links" on
                   Everything too, and the query never touched typed cards. */
                placeholder={linkTab && collection === 'link' ? 'Search your links' : 'Search the notebook'}
                aria-label={linkTab && collection === 'link' ? 'Search your links' : 'Search the notebook'}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <button
                type="button"
                data-test="nb-filters-toggle"
                className="nb-filters-btn"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen(o => !o)}
              >
                Filters
                {activeFilterCount > 0 && (
                  <span className="nb-filters-n" data-test="nb-filters-count">{activeFilterCount}</span>
                )}
              </button>
              {/* Card xUZFWf8JtHo2 item 1 — the numerator is the rendered count
                  and the base follows the tab, so it can never read "1 of 1"
                  above several cards. */}
              <span className="nb-result-count" data-test="nb-result-count">
                {visible.length} of {counterBase}
              </span>
              {/* Card 2wn9seit8zGi (PRD F5) — List / Grid rides the SAME row as
                  the count instead of claiming a row of its own: on a 900px
                  desktop viewport every chrome row above the list costs a card
                  of density, and the PRD's whole point is more cards on
                  screen. Desktop-only — grid is a use-of-width move and there
                  is no width to use on a phone (PRD L5). */}
              {viewToggle}
            </div>

            {filtersOpen && (
              <div className="nb-links-tools" data-test="nb-filters-panel">
                {/* Card vHyQrgOMJyPg — the row of kind chips is gone: one
                    search box, one Filters button, and ONE dropdown that
                    carries every category with its count. The count in the
                    option label is the narrowed one (same numbers the chips
                    showed), so a category with nothing matching reads an
                    honest 0 instead of disappearing. */}
                <select
                  className="nb-filter-select"
                  data-test="nb-kind-select"
                  aria-label="Category"
                  value={kindFilter}
                  onChange={e => { setKindFilter(e.target.value); setSubtypeFilter('all'); }}
                >
                  <option value="all">All categories ({chipLinks.length})</option>
                  {kindChips.map(c => (
                    <option key={c.kind} value={c.kind}>
                      {notebookRouteFor(c.kind).kindLabel} ({c.n})
                    </option>
                  ))}
                </select>
                {subtypeTabs.length > 0 && (
                  <select
                    className="nb-filter-select"
                    data-test="nb-subtype-select"
                    aria-label="Subtype"
                    value={subtypeFilter}
                    onChange={e => setSubtypeFilter(e.target.value)}
                  >
                    <option value="all">All subtypes</option>
                    {subtypeTabs.map(s => (
                      <option key={s} value={s}>{titleCase(s)}</option>
                    ))}
                  </select>
                )}
                <select
                  className="nb-filter-select"
                  data-test="nb-status-select"
                  aria-label="Saved, planned or done"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
                >
                  <option value="all">Everything</option>
                  <option value="saved">Saved</option>
                  <option value="planned">Planned</option>
                  <option value="done">Done</option>
                </select>
                <button
                  type="button"
                  data-test="nb-filters-clear"
                  className="nb-filters-clear"
                  disabled={activeFilterCount === 0}
                  onClick={clearFilters}
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
        )}

        {/* C5 — the saved-with-Undo line. A capture is a one-field action, so
            its confirmation has to be undoable in the same breath; the row is
            removed outright by its own id, never "the newest one". */}
        {captureToast && (
          <div className="nb-capture-toast" data-test="nb-capture-toast" role="status">
            <span className="nb-capture-toast-text">{captureToast.text}</span>
            {captureToast.itemId && (
              <button
                type="button"
                className="nb-undo-btn"
                data-test="nb-capture-undo"
                onClick={() => void undoCapture(captureToast.itemId as string)}
              >
                Undo
              </button>
            )}
            <button
              type="button"
              className="nb-receipt-x"
              aria-label="Dismiss"
              onClick={() => setCaptureToast(null)}
            >
              {/* Vectors only in the panel (walk-notebook-pills): the ✕ glyph
                  is a dingbat the "no emoji" sweep rightly catches, so the
                  toast dismiss draws the same X as an SVG. */}
              <svg {...ICON_ATTRS} width={12} height={12}>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Card xUZFWf8JtHo2 item 5 — the quiet remove's safety net. Removing a
            card leaves this line for a few seconds; Undo puts the row back. */}
        {removedItem && (
          <div className="nb-undo" data-test="nb-undo" role="status">
            <span className="nb-undo-text">
              Removed “{removedItem.label.length > 48 ? `${removedItem.label.slice(0, 48)}…` : removedItem.label}”.
            </span>
            <button
              type="button"
              className="nb-undo-btn"
              data-test="nb-undo-btn"
              disabled={busyId !== null}
              onClick={() => void undoRemove()}
            >
              Undo
            </button>
          </div>
        )}

        {/* PRD E3/E4 — the cluster strip, directly above the list it groups.
            Stacked card + count, expands IN PLACE; the cards themselves stay in
            the feed, so the list and its counter never change shape. */}
        {collection === 'all' && contextLive && (clusters.length > 0 || clusterPrefsDirty) && (
          <div className="nb-clusters" data-test="nb-clusters">
            {clusters.map(c => {
              const open = openCluster === c.key;
              const line = c.kind === 'place'
                ? `${c.label} · ${c.members.length} cards`
                : `${c.label} · ${c.members.length} cards${c.places > 0 ? ` · ${c.places} places` : ''}`;
              const addable = visible.filter(v =>
                v.addedBy === (me?.memberId ?? '\u0000') &&
                !c.members.some(m => m.itemId === v.itemId));
              return (
                <div key={c.key} className={`nb-cluster${open ? ' is-open' : ''}`}
                  data-test="nb-cluster" data-cluster-key={c.key}>
                  <button
                    type="button"
                    className="nb-cluster-head"
                    data-test="nb-cluster-toggle"
                    aria-expanded={open}
                    onClick={() => { setOpenCluster(open ? null : c.key); setClusterAddPick(''); }}
                  >
                    <span className="nb-stack" aria-hidden="true"><i /><i /><i /></span>
                    <span className="nb-cluster-line" data-test="nb-cluster-line">{line}</span>
                    <svg className="nb-cluster-caret" {...ICON_ATTRS} aria-hidden="true">
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                  </button>
                  {open && (
                    <div className="nb-cluster-body" data-test="nb-cluster-body">
                      <ul className="nb-cluster-members">
                        {c.members.map(m => (
                          <li key={m.itemId} className="nb-cluster-member">
                            <button
                              type="button"
                              className="nb-cluster-member-open"
                              data-test="nb-cluster-member"
                              data-item-id={m.itemId}
                              onClick={() => setContextId(m.itemId)}
                            >
                              <span className="nb-cluster-member-kind">{ENTRY_LABEL[m.entryType]}</span>
                              <span className="nb-cluster-member-title">{clusterTitleOf(m)}</span>
                            </button>
                            <button
                              type="button"
                              className="nb-cluster-member-remove"
                              data-test="nb-cluster-remove"
                              aria-label={`Remove ${clusterTitleOf(m)} from this group`}
                              onClick={() => removeClusterMember(c.key, m.itemId)}
                            >
                              <svg {...ICON_ATTRS}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="nb-cluster-actions">
                        <button
                          type="button"
                          className="pear-btn pear-btn-quiet nb-cluster-action"
                          data-test="nb-cluster-plan"
                          onClick={() => void planCluster(c)}
                        >
                          Plan this
                        </button>
                        <button
                          type="button"
                          className="pear-btn pear-btn-quiet nb-cluster-action"
                          data-test="nb-cluster-dates"
                          onClick={pickClusterDates}
                        >
                          Pick dates
                        </button>
                        {/* A place is a search away — a query link, never an
                            embedded map: no key, no API call, no spend. */}
                        {c.kind === 'place' && (
                          <a
                            className="pear-btn pear-btn-quiet nb-cluster-action"
                            data-test="nb-cluster-map"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.label)}`}
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            View on map
                          </a>
                        )}
                        <button
                          type="button"
                          className="pear-btn pear-btn-quiet nb-cluster-action"
                          data-test="nb-cluster-dissolve"
                          onClick={() => dissolveCluster(c.key)}
                        >
                          Dissolve
                        </button>
                      </div>
                      {addable.length > 0 && (
                        <div className="nb-cluster-add">
                          <select
                            className="nb-cluster-add-select"
                            data-test="nb-cluster-add-select"
                            aria-label="Card to add to this group"
                            value={clusterAddPick}
                            onChange={e => setClusterAddPick(e.target.value)}
                          >
                            <option value="">Add a card…</option>
                            {addable.map(a => (
                              <option key={a.itemId} value={a.itemId}>{clusterTitleOf(a)}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="pear-btn pear-btn-quiet nb-cluster-action"
                            data-test="nb-cluster-add"
                            disabled={!clusterAddPick}
                            onClick={() => addClusterMember(c.key, clusterAddPick)}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {clusterPrefsDirty && (
              <button
                type="button"
                className="nb-cluster-action nb-cluster-restore"
                data-test="nb-clusters-restore"
                onClick={restoreClusters}
              >
                Show hidden groups
              </button>
            )}
          </div>
        )}

        {/* A typed tab has no links bar, so the toggle keeps its own line. */}
        {!linkTab && viewToggle}

        <div
          className={`nb-list${viewMode === 'grid' && contextLive ? ' is-grid' : ''}`}
          data-test="nb-list"
          data-view={viewMode === 'grid' && contextLive ? 'grid' : 'list'}
          onKeyDown={feedKeyDown}
        >
          {items === null && !failed && <SkeletonBlock lines={3} />}

          {failed && <p className="nb-empty">Couldn't load your notebook.</p>}

          {items !== null && items.length === 0 && !failed && (
            <div className="nb-empty-state">
              <p className="nb-empty-title">Nothing saved yet.</p>
              {/* Card 7VIbSBeJhvbQ: the trailer / album line is LINK copy — it
                  only makes sense on the tabs that take a link. A typed tab gets
                  its own first-move line instead of being told to drop a link. */}
              <p className="nb-empty-hint">{EMPTY_HINT[collection] ?? EMPTY_HINT.all}</p>
            </div>
          )}

          {items !== null && items.length > 0 && visible.length === 0 && (
            <p className="nb-empty">No cards in this filter yet.</p>
          )}

          {visible.map(item => {
            /* Same canonical field the filter and the toggle use — the card's
               "is-done" state and the Done filter now read one value. */
            const done = item.status === 'done';
            const itemTags = tags.filter(t => t.itemId === item.itemId);
            const itemSuggs = suggestions.filter(s => s.itemId === item.itemId);
            // Notebook v2 typed entry — a plain-text card (gift/memory/date/note/idea).
            if (item.entryType !== 'link') {
              const isMeeting = item.provider === 'meeting';
              const label = isMeeting ? MEETING_LABEL : ENTRY_LABEL[item.entryType];
              const dur = durationLabel(item.durationSeconds);
              return (
                <article
                  className={`nb-card nb-item nb-typed nb-typed-${item.entryType}${isMeeting ? ' nb-typed-meeting' : ''}${contextId === item.itemId ? ' is-open' : ''}${justSavedId === item.itemId ? ' is-new' : ''}`}
                  key={item.itemId}
                  data-context-card={item.itemId}
                  data-test={isMeeting ? 'meeting-card' : undefined}
                  /* PRD §8 — the feed's one tab stop (roving): focus follows
                     the person as the Arrow keys walk the list. */
                  tabIndex={rovingCardId === item.itemId ? 0 : -1}
                  onFocus={() => setFeedRoving(item.itemId)}
                  /* PRD P1 — "selecting a card opens it in the panel". The card
                     is the target, but its own controls keep priority: a click
                     that started on a link, button or field is theirs, not the
                     panel's. The "Details" button is the keyboard path. */
                  onClick={e => {
                    if (!contextLive) return;
                    const t = e.target as HTMLElement | null;
                    if (t?.closest('a, button, input, select, textarea, label')) return;
                    setContextId(cur => (cur === item.itemId ? null : item.itemId));
                  }}
                >
                  <div className="nb-body">
                    <div className="nb-meta">
                      <span className={`nb-cat nb-cat-${item.entryType}`}>
                        <span className="nb-icon">{isMeeting ? MEETING_ICON : ENTRY_ICON[item.entryType]}</span>
                        {label}
                      </span>
                      {dur && <span className="nb-duration">{dur}</span>}
                      {/* Card wGXKzRwJE57v — "from the thread" only for genuine
                          thread-sourced entries. Every typed card used to carry
                          the tag because quick-adds also set addedBy. */}
                      {item.provider === 'thread' && <span className="nb-provider">from the thread</span>}
                      {item.shared === false && (
                        <span className="nb-private" title="Only you can see this">Just you</span>
                      )}
                      {/* Card xUZFWf8JtHo2 item 4 — the byline below already carries
                          the save date (day + time), so a separate "Added <day>"
                          line was the same date printed twice. The byline is the
                          superset: it keeps WHO saved it AND the exact moment, so
                          the standalone Added span is dropped on typed cards. Link
                          cards keep theirs — they have no byline to supersede it. */}
                      {/* Card SApyMHlbeyhg — WHO saved it, to the minute.
                          Card quWhYyrNiIbt item 4 — the meta row is one line now,
                          so a long name clips with an ellipsis; the untrimmed
                          string rides `title` and is still what the DOM holds. */}
                      <span
                        className="nb-byline"
                        data-test="nb-byline"
                        title={`By ${bylineAuthor(item.addedBy, members, me?.memberId ?? null)}, ${bylineLabel(item.createdAt)}`}
                      >
                        By {bylineAuthor(item.addedBy, members, me?.memberId ?? null)}, {bylineLabel(item.createdAt)}
                      </span>
                    </div>
                    {editingId === item.itemId ? (
                      /* Card 67zOnRgx4YTI item 1 — the saver edits their own
                         words in place instead of adding a second card. */
                      <div className="nb-edit" data-test="nb-edit-form">
                        <label className="nb-label" htmlFor={`nb-edit-${item.itemId}`}>
                          Edit {label.toLowerCase()}
                        </label>
                        <textarea
                          id={`nb-edit-${item.itemId}`}
                          className="pear-chat-input nb-input nb-edit-text"
                          rows={3}
                          ref={editTextRef}
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                        />
                        {editError && (
                          <p className="nb-save-error" data-test="nb-edit-error" role="alert">{editError}</p>
                        )}
                        <div className="nb-composer-foot">
                          {/* Card ggTQe2KbB0A3 MEDIUM-4 — the hint tells the
                              truth about WHO will see the edit: on a 'just
                              me' card the promise used to claim the partner
                              sees it, which is exactly backwards. */}
                          <span className="nb-hint">
                            {item.shared === false
                              ? 'Only you will see this change — the card stays where it is.'
                              : 'Your partner sees the change — the card stays where it is.'}
                          </span>
                          <div className="nb-composer-actions">
                            <button type="button" className="pear-btn" data-test="nb-edit-cancel" onClick={cancelEdit}>
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="pear-btn pear-btn-mustard"
                              data-test="nb-edit-save"
                              disabled={editBusy || editText.trim().length === 0}
                              onClick={() => void saveEdit(item)}
                            >
                              {editBusy ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : isMeeting ? (
                      // A transcript runs to thousands of characters — scroll it
                      // instead of letting one card swallow the whole list.
                      <div
                        className="nb-item-title nb-typed-body nb-meeting-body"
                        data-test="meeting-card-body"
                        style={{ maxHeight: 260, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.85rem', fontWeight: 400, lineHeight: 1.5 }}
                      >
                        {item.body ?? ''}
                      </div>
                    ) : (
                      <h3 className="nb-item-title nb-typed-body">{item.body ?? ''}</h3>
                    )}
                    {/* Gift enrichment (card OviOTaNsNivQ): the concierge's
                        acknowledgement the moment the idea is saved, then the
                        detail the search found once it lands. 'done' swaps the
                        ack for the detail — the promise has been kept. */}
                    {item.entryType === 'gift' && item.enrichStatus !== '' && (
                      <div className="gift-enrich" data-test="gift-enrich" data-status={item.enrichStatus}>
                        {item.enrichStatus !== 'done' && (
                          <p className="gift-ack" data-testid="gift-ack">{GIFT_ACK}</p>
                        )}
                        {item.enrichStatus === 'pending' && (
                          <p className="gift-wait" data-test="gift-wait">Developing an initial list of suggestions…</p>
                        )}
                        {item.enrichStatus === 'off' && (
                          <p className="gift-state" data-test="gift-off">
                            The concierge is off — turn it on in Settings → Concierge and I'll bring the suggestions.
                          </p>
                        )}
                        {item.enrichStatus === 'none' && (
                          <p className="gift-state" data-test="gift-none">
                            No matching suggestions came back this time — the idea stays on file.
                          </p>
                        )}
                        {item.enrichStatus === 'done' && item.enrichment && (
                          <>
                            <p className="gift-band" data-test="gift-band">
                              {item.enrichment.priceBand || 'Prices not listed'}
                            </p>
                            <p className="gift-why" data-test="gift-why">{item.enrichment.why}</p>
                            {item.enrichment.picks.length > 0 && (
                              <ul className="gift-picks" data-test="gift-picks">
                                {item.enrichment.picks.map(p => (
                                  <li key={p.url}>
                                    <a href={p.url} target="_blank" rel="noreferrer noopener">{p.title}</a>
                                    {p.price && <span className="gift-pick-meta">{p.price}</span>}
                                    {p.rating && <span className="gift-pick-meta">{p.rating}</span>}
                                  </li>
                                ))}
                              </ul>
                            )}
                            <p className="gift-source" data-test="gift-source">
                              {item.enrichment.source === 'serpapi' ? 'Google Shopping' : 'Amazon'} ·{' '}
                              {item.enrichment.count} match{item.enrichment.count === 1 ? '' : 'es'}
                            </p>
                          </>
                        )}
                      </div>
                    )}
                    {/* Card 2qBAZOM2LFWa — where and when, once it is known.
                         One line under the words: the day leads, the place
                         follows. Both are optional, so a card with neither
                         renders nothing at all — the detail is earned, not
                         demanded. */}
                    {(item.eventDate != null || item.place) && (
                      <p className="nb-detail-line" data-test="nb-detail-line">
                        {item.eventDate != null && (
                          <span className="nb-detail-when" data-test="nb-detail-when">
                            {new Date(item.eventDate).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })}
                          </span>
                        )}
                        {item.place && (
                          /* PRD E1 — the place is an entity chip wherever it
                             renders: same pin, same tint, one vocabulary for
                             "this is a place". The line still carries it, so
                             the day+place sentence stays whole. */
                          <span className="nb-detail-where nb-entity-chip" data-entity="place" data-test="nb-detail-where">
                            {PLACE_PIN}
                            {item.place}
                          </span>
                        )}
                      </p>
                    )}
                    {/* The detail form itself — one card open at a time. */}
                    {detailId === item.itemId && (
                      <div className="nb-detail-form" data-test="nb-detail-form">
                        <label className="nb-label" htmlFor={`nb-detail-date-${item.itemId}`}>
                          What day was it?
                        </label>
                        <input
                          id={`nb-detail-date-${item.itemId}`}
                          type="date"
                          className="pear-chat-input nb-input"
                          data-test="nb-detail-date"
                          value={detailDate}
                          onChange={e => setDetailDate(e.target.value)}
                        />
                        <label className="nb-label" htmlFor={`nb-detail-place-${item.itemId}`}>
                          Where?
                        </label>
                        <div className="nb-place-row">
                          <input
                            id={`nb-detail-place-${item.itemId}`}
                            className="pear-chat-input nb-input"
                            data-test="nb-detail-place"
                            placeholder="Search for a place, or type it in"
                            value={detailPlace}
                            onChange={e => {
                              setDetailPlace(e.target.value);
                              setPlaceQuery(e.target.value);
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void searchPlaces();
                              }
                            }}
                          />
                          <Pressable
                            className="pear-btn nb-place-search"
                            data-test="nb-place-search"
                            disabled={placeBusy || !placeQuery.trim()}
                            onClick={() => void searchPlaces()}
                          >
                            {placeBusy ? 'Searching…' : 'Search'}
                          </Pressable>
                        </div>
                        {placeNote && <p className="nb-hint" data-test="nb-place-note">{placeNote}</p>}
                        {placeResults.length > 0 && (
                          <ul className="nb-place-results" data-test="nb-place-results">
                            {placeResults.map(p => (
                              <li key={p.id}>
                                <button
                                  type="button"
                                  className="nb-place-result"
                                  data-test="nb-place-result"
                                  onClick={() => {
                                    setDetailPlace(placeFromPick(p));
                                    setPlaceResults([]);
                                    setPlaceNote(null);
                                  }}
                                >
                                  <span className="nb-place-name">{p.name}</span>
                                  {p.address && <span className="nb-place-addr">{p.address}</span>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {detailError && (
                          <p className="nb-save-error" data-test="nb-detail-error" role="alert">{detailError}</p>
                        )}
                        <div className="nb-composer-actions">
                          <Pressable className="pear-btn" data-test="nb-detail-cancel" onClick={closeDetail}>
                            Cancel
                          </Pressable>
                          <Pressable
                            className="pear-btn pear-btn-mustard"
                            data-test="nb-detail-save"
                            disabled={detailBusy}
                            onClick={() => void saveDetail(item)}
                          >
                            {detailBusy ? 'Saving…' : 'Save'}
                          </Pressable>
                        </div>
                      </div>
                    )}
                    <div className="nb-actions">
                      {/* Card 2wn9seit8zGi (PRD K4/P1) — the desktop affordance
                          that opens this card in the context panel. Hidden
                          below 760px, where there is no panel to open (PRD
                          L5), so it never renders as a dead control. */}
                      {contextLive && (
                        <Pressable
                          className="nb-open-panel"
                          aria-label={`Open ${label} in the details panel`}
                          data-test="nb-open-panel"
                          onClick={() => setContextId(item.itemId)}
                        >
                          View
                        </Pressable>
                      )}
                      {/* Card 67zOnRgx4YTI item 1 — Edit, owner only, same
                          gate as Remove: the words can be rewritten in place. */}
                      {canRemove(item) && editingId !== item.itemId && (
                        <Pressable
                          className="nb-edit-btn"
                          aria-label={`Edit ${label}`}
                          data-test="nb-edit"
                          disabled={editBusy}
                          onClick={() => startEdit(item)}
                        >
                          Edit
                        </Pressable>
                      )}
                      {/* Card 2qBAZOM2LFWa — the detail pill. Small, owner-only
                          (it writes, so it sits on the same gate as Edit), and
                          it says what it holds once something is held: a card
                          with a day reads "14 Oct", so the pill is the card's
                          own summary and not a mystery control. */}
                      {canRemove(item) && (
                        <Pressable
                          className={`nb-detail-btn${(item.eventDate != null || item.place) ? ' has-detail' : ''}`}
                          aria-label={`Edit the detail on this ${label.toLowerCase()}`}
                          aria-expanded={detailId === item.itemId}
                          data-test="nb-detail"
                          disabled={detailBusy}
                          onClick={() => (detailId === item.itemId ? closeDetail() : openDetail(item))}
                        >
                          {item.eventDate != null
                            ? new Date(item.eventDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })
                            : item.place
                              ? item.place
                              : 'Detail'}
                        </Pressable>
                      )}
                      {/* Visibility pill (card quWhYyrNiIbt, PRD K3) — one
                          control that reads the card's audience and flips it
                          from a small menu. Replaces the old share/make-private
                          pair; owner-only, same confirm + PUT behind it. */}
                      {canRemove(item) && (
                        <VisibilityPill
                          shared={item.shared !== false}
                          partnerName={partnerNameOf(members, me?.memberId ?? null)}
                          busy={busyId === item.itemId}
                          onSet={next => void (next ? shareEntry(item) : makePrivate(item))}
                        />
                      )}
                      {canRemove(item) && (
                        <Pressable
                          className="nb-delete"
                          aria-label={`Delete ${label}`}
                          onClick={() => void deleteEntry(item)}
                        >
                          Remove
                        </Pressable>
                      )}
                    </div>
                  </div>
                </article>
              );
            }
            return (
              /* Sol review 2026-10-19, card xNGOA5rZz9LV phase 2 — the calm
                 card face. A default visit now reads: thumbnail, title, one
                 source line, the private badge, and Open / Done. Everything
                 that classifies or explains the card (kind verdict, home,
                 subtype, added date, description, rationale, tags, filing
                 picker, Remove) sits behind one Details disclosure, so the
                 list scans instead of audits. Suggestions keep a count-bearing
                 opener on the face — a pending scan still announces itself. */
              <article
                className={`nb-card nb-item${done ? ' is-done' : ''}${contextId === item.itemId ? ' is-open' : ''}${justSavedId === item.itemId ? ' is-new' : ''}`}
                key={item.itemId}
                data-context-card={item.itemId}
                /* Same selection contract as the typed cards above, plus the
                   PRD §8 roving tab stop the Arrow keys move. */
                tabIndex={rovingCardId === item.itemId ? 0 : -1}
                onFocus={() => setFeedRoving(item.itemId)}
                onClick={e => {
                  if (!contextLive) return;
                  const t = e.target as HTMLElement | null;
                  if (t?.closest('a, button, input, select, textarea, label')) return;
                  setContextId(cur => (cur === item.itemId ? null : item.itemId));
                }}
              >
                {item.imageUrl && (
                  <img className="nb-art" src={item.imageUrl} alt="" loading="lazy" />
                )}
                <div className="nb-body">
                  {editingId === item.itemId ? (
                    /* Card 67zOnRgx4YTI item 1 — link cards edit both the name
                       and the words around it, on the face where they read. */
                    <div className="nb-edit" data-test="nb-edit-form">
                      <label className="nb-label" htmlFor={`nb-edit-title-${item.itemId}`}>Title</label>
                      <input
                        id={`nb-edit-title-${item.itemId}`}
                        className="pear-chat-input nb-input"
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                      />
                      <label className="nb-label" htmlFor={`nb-edit-${item.itemId}`}>Description</label>
                      <textarea
                        id={`nb-edit-${item.itemId}`}
                        className="pear-chat-input nb-input nb-edit-text"
                        rows={3}
                        ref={editTextRef}
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                      />
                      {editError && (
                        <p className="nb-save-error" data-test="nb-edit-error" role="alert">{editError}</p>
                      )}
                      <div className="nb-composer-foot">
                        {/* Card ggTQe2KbB0A3 MEDIUM-4 — same truth on link
                            cards: a 'just me' link edits privately. */}
                        <span className="nb-hint">
                          {item.shared === false
                            ? 'Only you will see this change — the link stays where it is.'
                            : 'Your partner sees the change — the link stays where it is.'}
                        </span>
                        <div className="nb-composer-actions">
                          <button type="button" className="pear-btn" data-test="nb-edit-cancel" onClick={cancelEdit}>
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="pear-btn pear-btn-mustard"
                            data-test="nb-edit-save"
                            disabled={editBusy || editTitle.trim().length === 0}
                            onClick={() => void saveEdit(item)}
                          >
                            {editBusy ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <h3 className="nb-item-title">{item.title ?? hostOf(item.url)}</h3>
                  )}
                  {/* The one source line: where it came from and how long it
                      runs. The provider name leads when we know it; an
                      unknown provider falls back to the host. */}
                  <p className="nb-url">
                    {item.provider === 'unknown' ? hostOf(item.url) : item.provider}
                    {durationLabel(item.durationSeconds) ? ` · ${durationLabel(item.durationSeconds)}` : ''}
                  </p>
                  {/* PRD K1/E1 — the entity chip on a link card's face: the
                      place the row already holds, so a link that names a
                      place says so without opening it. A card with no place
                      renders nothing here — the text stays as it was. */}
                  {item.place && (
                    <div className="nb-entity-chips" data-test="nb-entity-chips">
                      <span className="nb-entity-chip" data-entity="place" data-test="nb-entity-chip">
                        {PLACE_PIN}
                        {item.place}
                      </span>
                    </div>
                  )}
                  {/* The theme, in plain words (Jonathan 2026-10-20): the card's
                      one line of value, read through the editorial label so the
                      stored key stays canonical and searchable. A theme the scan
                      proposed reads as the card's own sentence rather than as a
                      chore to accept — accepting it is still what files it. */}
                  {(() => {
                    const accepted = itemTags.find(t => t.kind === 'theme');
                    const proposed = itemSuggs.find(s => s.kind === 'theme');
                    const theme = accepted ?? proposed;
                    if (!theme) return null;
                    return (
                      <p
                        className="nb-theme"
                        data-source={accepted ? 'agent' : 'suggested'}
                        data-theme={theme.tag}
                      >
                        {tagText('theme', theme.tag)}
                      </p>
                    );
                  })()}
                  {/* A link that is not shared got that way on purpose (the gift
                      toggle, card PQX46vNPdbwn) — the badge stays on the face,
                      because who can see a card is the one fact worth keeping
                      visible. */}
                  {!item.shared && (
                    <div className="nb-meta">
                      <span className="nb-private" title="Only you can see this">Just you</span>
                    </div>
                  )}

                  {/* The chooser (card xNGOA5rZz9LV phase 3, sol review
                      2026-10-19 high): the cascade's 'ask' band persisted its
                      ranked candidates but nothing on screen ever consumed
                      them, so an unresolved link kept a filed-looking card and
                      the only correction on offer spoke the legacy media
                      categories — which cannot reach Events, Places, Things or
                      Trips. The options are now buttons on the face (best
                      guess first) and "Somewhere else" opens the full list.
                      Answering clears the band server-side, so the chooser
                      leaves with the question it answered. */}
                  {item.kindTier === 'ask' && (
                    <div className="nb-chooser" data-test="nb-chooser">
                      <p className="nb-chooser-head" data-test="nb-chooser-head">
                        Not sure where this files — which is it?
                      </p>
                      {(item.kindOptions ?? []).length > 0 && (
                        <div className="nb-chooser-opts">
                          {(item.kindOptions ?? []).map((o, i) => {
                            const label = `${notebookRouteFor(o.kind).kindLabel}${o.subtype ? ` › ${titleCase(o.subtype)}` : ''}`;
                            return (
                              <button
                                type="button"
                                key={`${o.kind}-${o.subtype ?? ''}`}
                                data-test={`nb-chooser-opt-${i}`}
                                className="nb-chooser-opt"
                                disabled={busyId === item.itemId}
                                onClick={() => void fileKind(item, o.kind, o.subtype, label)}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      <div className="nb-chooser-more">
                        <label className="nb-chooser-more-label" htmlFor={`nb-chooser-kind-${item.itemId}`}>
                          Somewhere else
                        </label>
                        <select
                          id={`nb-chooser-kind-${item.itemId}`}
                          className="nb-chooser-kind"
                          data-test="nb-chooser-kind"
                          aria-label="File this link under another kind"
                          value=""
                          disabled={busyId === item.itemId}
                          onChange={e => {
                            const k = e.target.value;
                            if (k) void fileKind(item, k, null, notebookRouteFor(k).kindLabel);
                          }}
                        >
                          <option value="" disabled>Choose a kind…</option>
                          {NOTEBOOK_LINK_KINDS.map(k => (
                            <option key={k} value={k}>{notebookRouteFor(k).kindLabel}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* A failure on THIS card, said on this card (phase 3). */}
                  {rowError?.itemId === item.itemId && (
                    <p className="nb-save-error nb-row-error" data-test="nb-card-error" role="alert">{rowError.message}</p>
                  )}

                  <div className="nb-actions">
                    <a className="nb-intent" href={item.url} target="_blank" rel="noreferrer noopener">
                      {INTENT_VERB[item.intent]} ↗
                    </a>
                    {/* Card 2wn9seit8zGi — same desktop affordance the typed
                        cards carry: open this card in the context panel. */}
                    {contextLive && (
                      <Pressable
                        className="nb-open-panel"
                        aria-label={`Open ${item.title?.trim() || hostOf(item.url)} in the details panel`}
                        data-test="nb-open-panel"
                        onClick={() => setContextId(item.itemId)}
                      >
                        View
                      </Pressable>
                    )}
                    <Pressable
                      className={`nb-done${done ? ' is-on' : ''}`}
                      disabled={busyId === item.itemId}
                      onClick={() => void toggleDone(item)}
                      aria-pressed={done}
                      data-test="nb-done"
                    >
                      {/* One verb, said once (Jonathan 2026-10-20): the CTA
                          beside this already reads "Read ↗", so the done
                          control must not say "Mark read" next to it. The
                          context-aware verb survives wherever it adds
                          information (Watch / Listen) and collapses to Done
                          where it would only repeat the CTA. */}
                      {(() => {
                        const verb = doneVerb(item.kind);
                        const repeatsCta = verb.toLowerCase() === INTENT_VERB[item.intent].toLowerCase();
                        if (repeatsCta) return done ? '✓ Done' : 'Mark done';
                        return done ? `✓ ${verb}` : `Mark ${verb.toLowerCase()}`;
                      })()}
                    </Pressable>
                    {/* A pending scan still has to be reachable from the face,
                        but as a count rather than a wall of editors. */}
                    {itemSuggs.length > 0 && (
                      <button
                        type="button"
                        className="nb-suggs-toggle"
                        data-test="nb-suggs-toggle"
                        aria-expanded={suggsOpen.has(item.itemId)}
                        onClick={() => setSuggsOpen(s => toggleId(s, item.itemId))}
                      >
                        {itemSuggs.length} suggested tag{itemSuggs.length === 1 ? '' : 's'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="nb-details-btn"
                      data-test="nb-details-toggle"
                      aria-expanded={detailsOpen.has(item.itemId)}
                      onClick={() => setDetailsOpen(s => toggleId(s, item.itemId))}
                    >
                      Details
                    </button>
                    {/* Card 67zOnRgx4YTI item 1 — Edit, owner only: the name
                        and the words around it can be rewritten in place. */}
                    {canRemove(item) && editingId !== item.itemId && (
                      <button
                        type="button"
                        className="nb-edit-btn"
                        data-test="nb-edit"
                        disabled={editBusy}
                        onClick={() => startEdit(item)}
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {itemSuggs.length > 0 && suggsOpen.has(item.itemId) && (
                    <div className="nb-suggs" data-test="nb-suggs">
                      <p className="nb-suggs-head">Suggested — nothing is tagged until you say so</p>
                      {itemSuggs.map(s => (
                        <div className="nb-sugg" key={s.id}>
                          <p className="nb-sugg-why">
                            <span className="nb-sugg-tag">{TAG_KIND_LABEL[s.kind]} · {tagText(s.kind, s.tag)}</span>
                            {s.subject && (
                              <span className="nb-sugg-subject" data-subject={s.subject}>
                                about {s.subject}
                              </span>
                            )}
                            {s.rationale}
                          </p>
                          <div className="nb-sugg-actions">
                            <Pressable
                              className="nb-sugg-use"
                              disabled={busySugg === s.id}
                              onClick={() => void resolveSuggestion(s.id, 'accept')}
                            >
                              {busySugg === s.id ? 'Saving…' : 'Use this tag'}
                            </Pressable>
                            <Pressable
                              className="nb-sugg-mine"
                              disabled={busySugg === s.id}
                              aria-expanded={overriding === s.id}
                              onClick={() => {
                                setOverriding(overriding === s.id ? null : s.id);
                                setOverrideText('');
                              }}
                            >
                              My own word
                            </Pressable>
                          </div>
                          {overriding === s.id && (
                            <form
                              className="nb-sugg-form"
                              onSubmit={e => {
                                e.preventDefault();
                                const v = overrideText.trim();
                                if (v) void resolveSuggestion(s.id, 'override', v);
                              }}
                            >
                              <input
                                className="pear-chat-input nb-sugg-input"
                                value={overrideText}
                                maxLength={40}
                                placeholder="Your word"
                                aria-label={`Your ${TAG_KIND_LABEL[s.kind].toLowerCase()} tag`}
                                onChange={e => setOverrideText(e.target.value)}
                              />
                              <button
                                type="submit"
                                className="pear-btn pear-btn-mustard nb-sugg-save"
                                disabled={overrideText.trim().length === 0}
                              >
                                Save
                              </button>
                            </form>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Everything that explains or refiles the card. Hidden by
                      default, never unmounted — the date line and the filing
                      picker are still on every card, one tap away. */}
                  <div
                    className={`nb-details${detailsOpen.has(item.itemId) ? ' is-open' : ''}`}
                    data-test="nb-details"
                  >
                    <div className="nb-meta">
                      {/* One verdict per card (founder email 2026-10-19): the kind
                          filing IS the classification — showing the legacy
                          media-category chip beside it left cards that read
                          "Something else" while filing to Reading. The kind
                          label leads; the legacy category only shows when no
                          kind verdict exists yet (kind 'other'). */}
                      {/* Card FsDDh1AF6Ege — when it was saved, on every card. */}
                      <span className="nb-added" data-test="nb-date-added">Added {addedLabel(item.createdAt)}</span>
                      {/* Where the card filed (card 4CXp7HfSIAGd): named only when
                          a route moved it off the catch-all kind, so an ordinary
                          paste looks exactly as before. */}
                      {item.kind && item.kind !== 'other' && (
                        <span className="nb-home" data-home={item.kind}>{notebookRouteFor(item.kind).homeLabel}</span>
                      )}
                      {/* The subtype tab the card answers to (PRD §5 layer 2). */}
                      {item.subtype && (
                        <span className="nb-subtype-chip" data-subtype={item.subtype}>{titleCase(item.subtype)}</span>
                      )}
                    </div>
                    {/* The ".nb-concierge" promise row is gone (sol review
                        2026-10-19, card xNGOA5rZz9LV): it promised a curated
                        recommendations pass the product does not run. */}
                    {item.description && <p className="nb-desc">{item.description}</p>}

                    {/* The host-proof sentence is provenance, so it yields to
                        the theme line when one exists (Jonathan 2026-10-20:
                        "instead of just saying how it arrived at the category,
                        give a brief indication of theme"). It stays for cards
                        with no theme, and always in the refile path. */}
                    {item.categoryReason && !itemTags.some(t => t.kind === 'theme') && !itemSuggs.some(s => s.kind === 'theme') && (
                      <p className="nb-reason" title="Why this card was categorised this way">
                        {item.categoryReason}
                        {item.categorizedBy === null && item.category !== 'other' ? ' · suggested' : ' · yours'}
                      </p>
                    )}

                    {itemTags.length > 0 && (
                      <div className="nb-tags">
                        {itemTags.map(t => (
                          <span className="nb-tag" key={t.id} data-source={t.source}>
                            {TAG_KIND_LABEL[t.kind]} · {tagText(t.kind, t.tag)}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="nb-details-actions">
                      <select
                        className="nb-retag"
                        aria-label="Change filing"
                        value={item.category}
                        disabled={busyId === item.itemId}
                        onChange={e => void retag(item, e.target.value as Category)}
                      >
                        {(Object.keys(CATEGORY_LABEL) as Category[]).map(c => (
                          <option key={c} value={c}>
                            {/* One vocabulary (Jonathan 2026-10-20): the option
                                names the shelf once. "Article — Read" repeated
                                the same verdict in two taxonomies. */}
                            {CATEGORY_TO_KIND[c] !== 'other' ? notebookRouteFor(CATEGORY_TO_KIND[c]).kindLabel : CATEGORY_LABEL[c]}
                          </option>
                        ))}
                      </select>
                      {/* Visibility pill (card quWhYyrNiIbt, PRD K3) — links get
                          the same single audience control typed entries carry.
                          Owner-only, same confirm + PUT behind it. */}
                      {canRemove(item) && (
                        <VisibilityPill
                          shared={!!item.shared}
                          partnerName={partnerNameOf(members, me?.memberId ?? null)}
                          busy={busyId === item.itemId}
                          onSet={next => void (next ? shareEntry(item) : makePrivate(item))}
                        />
                      )}
                      {/* Card FsDDh1AF6Ege — links get the same destructive row the
                          typed cards carry. Same confirm, same PUT removed:true.
                          Card Q4BluQX8fXgj — only the creator sees Remove. */}
                      {canRemove(item) && (
                        <Pressable
                          className="nb-delete"
                          aria-label={`Delete ${CATEGORY_LABEL[item.category]} card`}
                          disabled={busyId === item.itemId}
                          onClick={() => void deleteEntry(item)}
                        >
                          Remove
                        </Pressable>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {me === null && <p className="nb-empty">Log in to see the shared notebook.</p>}

        {/* PRD §6.1: "Saved to your date ideas — it'll come up when you plan
            together." The same portalled celebration primitive the kudos card
            and the chore tick-off use (card FktjQ7CT4keo), text-only. */}
        {dateSaved && (
          <KudosToast
            glyph="💡"
            label="Saved to your date ideas — it'll come up when you plan together."
            variant="shimmer"
          />
        )}
      </SubPanel>
      </div>{/* /.nb-feed */}

      {/* Card 2wn9seit8zGi (PRD L1/L4/P1) — the third zone. Renders only at
          >=760px; at >=1100px it is the sticky right column, between 760 and
          1100 it becomes the slide-over. Below that it does not exist and the
          phone keeps the layout it has always had. */}
      {contextLive && (
        <NotebookContext
          item={(items ?? []).find(i => i.itemId === contextId) ?? null}
          suggestions={suggestions}
          recent={[...(items ?? [])].sort((a, b) => b.createdAt - a.createdAt).slice(0, 4)}
          members={members}
          meId={me?.memberId ?? null}
          busy={busyId !== null || editBusy}
          onClose={() => setContextId(null)}
          onOpen={id => setContextId(id)}
          onDone={item => void toggleDone(item)}
          onRemove={item => void deleteEntry(item)}
          onEdit={item => startEdit(item)}
          onPlaceSave={(item, place) => savePlace(item, place)}
        />
      )}
    </div>
  );
}